/**
 * iter389 — Nutramilo Landing Page (full rebuild).
 *
 * 5-group skim-first architecture per design_guidelines.json:
 *   1. HeroOutcomeWall (hero + 3 testimonials + Audience Tabs + MDR badge + pricing teaser)
 *   2. HowItWorks       (3-step + 15s animated demo loop)
 *   3. SeeItInAction    (Patient/Doctor/Clinic personalised — reads AudienceContext)
 *   4. ScienceTrustWall (Featured Doctors — fetches /api/research/experts)
 *   5. PricingFinalCTA  (pricing teaser + final hero CTA + MDR disclaimer)
 *   + StickyBottomCTA   (appears post-hero on scroll)
 *
 * Brand discipline (strictly from logo):
 *   - Gradient primary: #5e227f → #9c39a1 → #e44288   (tech/AI signal)
 *   - Gradient nature:  #0e5c60 → #68c349 → #b2e772   (biology signal)
 *   - Solid:            #287a2e                       (CTA + wordmark)
 *
 * Compliance: Hero shows MDR badge (data-testid="hero-md-badge"). Final CTA repeats
 * the "Not a medical device" disclaimer.
 *
 * Bilingual (BG primary, EN secondary). Light + dark mode via existing ThemeContext.
 */
import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { AudienceProvider, useAudience, AUDIENCES } from "@/context/AudienceContext";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import LiveTwinDemo from "@/components/LiveTwinDemo";
import {
  TwinNetworkSection, ViciousCirclesSection, PlatformCapabilitiesSection,
} from "@/components/landing/TwinNetworkSections";
import { applyTextOverrides, resolveOrder } from "@/lib/landingSchema";
import FounderBand from "@/components/landing/FounderBand";
import PublicReviewsStrip from "@/components/PublicReviewsStrip";  // iter1114
import {
  Shield, Sparkles, ArrowRight, Check, Camera, LineChart, Brain,
  Stethoscope, Building2, User, Sun, Moon, Globe, FlaskConical,
  BookOpen, Activity, MessageCircle, FileSearch, Microscope, ScanLine,
  ChevronDown, Utensils, Dna, FlaskRound, ClipboardList, Bot, Menu, X,
  Volume2, VolumeX,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import "@/styles/landing-brand.css";

// iter416 — code-splitting: under-the-fold widgets са lazy-loaded.
// Initial JS bundle намален от ~480KB → ~190KB (по-малко при slow 3G).
const LandingDilCalculator = lazy(() => import("@/components/LandingDilCalculator"));
const LandingMealAI = lazy(() => import("@/components/LandingMealAI"));
const LandingNutritionQuiz = lazy(() => import("@/components/LandingNutritionQuiz"));
const LandingInsulinFoodGallery = lazy(() => import("@/components/LandingInsulinFoodGallery"));
const LandingPersonalInsulinLoad = lazy(() => import("@/components/LandingPersonalInsulinLoad"));
// iter733 — Photo scanner + COL3 game are now on standalone pages:
//   • /tools/scanner  → PhotoScannerPage → renders LandingNutritionLabelWidget
//   • /tools/collagen → CollagenPage    → renders LandingCollagenType3Widget
// Landing shows only a compact teaser (see <ToolsTeaser /> below).

// iter416 — CLS-safe placeholder, държи виsoтата на section-а докато lazy chunk
// се сваля, за да не "скача" page layout при slow networks.
function WidgetSkeleton({ isDark }) {
  return (
    <section
      data-testid="landing-widget-skeleton"
      aria-hidden="true"
      className={`relative my-12 sm:my-16 rounded-3xl border overflow-hidden animate-pulse ${
        isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,7%)]" : "border-slate-200 bg-white"
      }`}
      style={{ minHeight: "400px" }}
    >
      <div className={`px-6 sm:px-10 py-6 border-b ${isDark ? "border-[hsl(223,47%,15%)]" : "border-slate-100"}`}>
        <div className={`h-3 w-32 rounded ${isDark ? "bg-slate-700" : "bg-slate-200"} mb-2`} />
        <div className={`h-6 w-3/4 rounded ${isDark ? "bg-slate-700" : "bg-slate-200"}`} />
      </div>
      <div className="px-6 sm:px-10 py-8 space-y-3">
        <div className={`h-4 w-full rounded ${isDark ? "bg-slate-800" : "bg-slate-100"}`} />
        <div className={`h-4 w-5/6 rounded ${isDark ? "bg-slate-800" : "bg-slate-100"}`} />
        <div className={`h-32 w-full rounded ${isDark ? "bg-slate-800" : "bg-slate-100"} mt-4`} />
      </div>
    </section>
  );
}

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────
// Copy (BG primary, EN secondary)
// ─────────────────────────────────────────────────────────────────────
export const COPY = {
  bg: {
    nav: { science: "Наука", pricing: "Цени", assessment: "Безплатна оценка", guide: "Ръководство", faq: "ЧЗВ", compare: "Сравнение", signin: "Вход", signup: "Регистрация", dashboard: "Моето здраве", how: "Как работи", smart: "Умно хранене", about: "За нас", consultants: "Консултанти", tools: "Инструменти" },
    sources: {
      eyebrow: "Нашите източници",
      title: "Данни от водещи научни бази",
      sub: "Всяка препоръка в Nutramilo е базирана на достоверни, публично достъпни и рецензирани източници. Не измисляме — проверяваме.",
      items: [
        { name: "DrugBank", desc: "Лекарствено-хранителни взаимодействия за 41+ медикамента" },
        { name: "PubMed (NCBI)", desc: "30M+ медицински публикации — PMID, абстракти, автори, цитати" },
        { name: "Semantic Scholar", desc: "200M+ научни статии с цитирания и метаданни — Graph API" },
        { name: "PubChem (NIH)", desc: "Химична информация за хранителни добавки и съставки" },
        { name: "Open Food Facts", desc: "Баркод данни за хиляди продукти от магазина" },
        { name: "USDA FoodData", desc: "Хранителен състав на 23,000+ храни от USDA" },
        { name: "ClinVar (NCBI)", desc: "Генетични варианти и клинично значение" },
        { name: "Open Targets", desc: "Генни асоциации с болести и мишени" },
        { name: "OpenAI GPT-5.2", desc: "Разчитане на текст от четири отделни помощника" },
        { name: "Edamam", desc: "Рецепти и хранителен анализ" },
      ],
    },
    hero: {
      eyebrow: "Без платежна карта · 30 дни безплатно",
      headline_1: "Разберете какво прави",
      headline_2: "всяко хранене с тялото Ви.",
      sub: "Записвате чинията — със снимка, с глас или на ръка. За една минута виждате колко натоварва тялото, кое точно го качва и какво да смените на следващото хранене.",
      cta_primary: "Опитайте с едно хранене",
      cta_secondary: "Как работи",
      trust_1: "Отговор за 1 минута",
      trust_2: "Без регистрация за първия опит",
      trust_3: "Данните Ви остават Ваши",
      proof: "Отговорът стъпва върху 20 физиологични близнака и над 110 връзки между тях — затова е по-точен от обикновено броене на калории.",
      md_badge: "Образователен инструмент · не е медицинско изделие · MDR 2017/745",
      card_kicker: "Как се отрази днешното хранене",
      card_verdict: "Днес храната е лека за тялото",
      card_expl: "Изразходвахте само 24% от дневния си ориентир. Ако продължите така, това е денят, който помага.",
      card_nis: "Спокоен инсулинов отговор — храната се поема плавно.",
      card_cta: "Вижте днешния ден",
      card_score: "24 от 100",
      card_score_label: "натоварване (DIL)",
      card_caption: "Точно това вижда потребителят след първото записано хранене.",
    },
    steps: {
      label: "Три стъпки",
      title: "От чиния до ясен отговор",
      items: [
        { title: "Запишете храненето", desc: "Снимка на чинията, глас или търсене по име. Отнема по-малко от минута." },
        { title: "Вижте отговора", desc: "Един ясен резултат с думи: колко натоварва, кое го качва и какво да смените." },
        { title: "Питайте защо", desc: "Разговаряте с близнаците за хормоните, черния дроб, съня — върху Вашите данни." },
      ],
      why_label: "Защо отговорът е точен",
      why_title: "Зад простия отговор стои цялата архитектура",
      why: [
        "20 физиологични близнака — метаболизъм, хормони, черен дроб, бъбреци, сън, гръдно здраве и още.",
        "Над 110 връзки между тях — едно въведено число стига до всеки близнак, който го засяга.",
        "Откриване на порочни кръгове — когато два проблема се подхранват взаимно.",
        "Източници и прагове от научни бази, с посочени цитати за всеки праг.",
      ],
    },
    testimonials: [
      { id: "maria", name: "Мария, 47 г.", text: "За 30 дни: DIL от 68 → 41. Събуждам се без умора." },
      { id: "petar", name: "Петър, 52 г.", text: "За 30 дни: талия −4 см. Спрях следобедното сладко." },
      { id: "elena", name: "Елена, 38 г.", text: "За 30 дни: разбрах защо огладнявам в 16 ч. Смених 2 закуски." },
    ],
    testimonial_badge: "30 дни",
    audience_tabs: {
      title: "Кой сте Вие?",
      patient: "Аз съм пациент", doctor: "Аз съм лекар", clinic: "Аз съм клиника",
    },
    platform: {
      eyebrow: "Платформата",
      title: "Три интелигентни слоя. Една цялостна картина.",
      sub: "Всяка възможност захранва мрежата от близнаци — от храненето до изследванията и моделите за отделните органи.",
      learn: "Научи повече",
      layers: [
        { id: "health", title: "Health Intelligence", tagline: "Цялата картина на здравето Ви — живо табло, разчитане на данните, изводи и очаквания." },
        { id: "nutrition", title: "Nutrition Intelligence", tagline: "Кои храни работят най-добре за Вашето тяло — и какво прави всяко хранене с Вас." },
        { id: "precision", title: "Precision Health", tagline: "Лаборатории, скрининги и органно-специфични модели — прецизност отвъд храненето." },
      ],
      features: [
        {
          id: "health-intelligence",
          layer: "health",
          title: "Health Intelligence",
          desc: "Лично табло за обмяната: телесен индекс, висцерален показател, биологична възраст, седмична посока и изводи.",
          learnMore: "Разделът обединява всички Ваши данни в едно живо табло — телесен състав, показатели на обмяната, посока от дневника, последни изследвания и изводи. Виждате как се променя биологичната Ви възраст, кое Ви влияе най-силно и какво си заслужава да промените следващата седмица.",
          linkBg: "Вижте таблото",
        },
        {
          id: "nutrition-lab",
          layer: "nutrition",
          title: "Nutrition Intelligence Lab",
          desc: "Кои храни работят най-добре за Вашето тяло.",
          learnMore: "Избирате състоянията и целите си — разделът подрежда храните по личното Ви съответствие с тях и с едно натискане получавате седмично меню точно от тях. Зад това стоят 20 000+ оценени храни, 20+ здравословни състояния, научни метаболитни пътища (KEGG) и Nutramilo индексите (Инсулиногенен бюджет, Инсулинов отговор, COL3 и др.).",
          linkBg: "Отвори Nutrition Lab",
        },
        {
          id: "diary",
          layer: "nutrition",
          title: "Хранителен дневник",
          desc: "Снимайте чинията — продуктите се разпознават за 3 секунди и автоматично изчислява индексите.",
          learnMore: "За всяко хранене виждате как ще се отрази на инсулина и колко товар добавя за деня. Без непрекъснато измерване и без кръвни изследвания — за секунди.",
          linkBg: "Вижте дневника",
        },
        {
          id: "analyses",
          layer: "health",
          title: "Анализи",
          desc: "Телесният Ви състав, енергийният профил и как се променяте във времето.",
          learnMore: "Цялостен анализ на телесния състав, метаболитното здраве и биологичната възраст. Без скенери — само на база лабораторни тестове и измервания, които вече имаш. Резултатите се проследяват във времето, така че виждаш реалния си напредък.",
          linkBg: "Вижте разчитането",
        },
        {
          id: "ai",
          layer: "health",
          title: "Попитайте близнаците",
          desc: "Разговор с памет за Вашите данни, на български, в няколко стъпки.",
          learnMore: "Разговорът помни предишните Ви въпроси, лабораторни резултати и хранителни навици. Можете да попитате: 'Защо HOMA-IR ми не пада?' или 'Кога да се храня след натоварване?' — получавате конкретен отговор според Вашите данни.",
          linkBg: "Започнете разговор",
        },
        {
          id: "labs",
          layer: "precision",
          title: "Лаборатории (OCR)",
          desc: "Качете файла с резултатите — стойностите се извличат сами: HOMA-IR, липиди, hsCRP, витамини, ALT.",
          learnMore: "Поддържат се български лаборатории (СМДЛ, Цибалаб, Бодимед и др.) + международни. OCR извлича 30+ маркера и ги нанася във Вашия профил автоматично. Чувствителните маркери винаги получават коментар за валидиране.",
          linkBg: "Опитай",
        },
        {
          id: "screen",
          layer: "precision",
          title: "Кратки визуални проверки",
          desc: "Кожа, зъби, гърда — подпомогнати визуални проверки. Не заменят лекар.",
          learnMore: "Три отделни модула: скрининг за злокачествени промени по кожата (дерматология), визуална оценка на зъбен статус (пародонтит, кариес), и помощник за самоизследване на гърди. Всички с ясни disclaimers и насочване към специалист.",
          linkBg: "Вижте проверките",
          subLinks: [
            { label: "Кожа", to: "/skin-screening" },
            { label: "Зъби", to: "/dental-screening" },
            { label: "Гърди", to: "/health-intelligence/breast" },
          ],
        },
      ],
    },
    twin_core: {
      eyebrow: "Ядрото на платформата",
      title: "Как се задвижва мрежата от близнаци?",
      sub: "Всичко, което въвеждате, се влива в живо отражение на организма Ви. Всяко число стига до близнаците, които зависят от него.",
      inputs: ["Хранене", "Изследвания", "Сън", "Движение", "Оплаквания"],
      ai_label: "Физиологични правила и клинични формули",
      twin_label: "Мрежа от 20 близнака",
      out_label: "Оценка с посочен източник и следваща стъпка",
      note: "Мрежата се обновява при всяко хранене, измерване или изследване — затова оценката отразява днешното Ви състояние, а не състоянието отпреди месец.",
      cta: "Вижте мрежата в действие",
    },
    twins: {
      eyebrow: "20-те дигитални близнаци",
      title: "Не един, а цял екип близнаци за теб",
      sub: "Всеки близнак следи различна система на тялото. Заедно образуват пълна картина на здравето — с физиологични връзки помежду си, които се преизчисляват при всеки нов запис.",
      list: [
        { emoji: "🍽️", name: "Хранителен", desc: "DIL, въглехидрати, фибри, омега баланс" },
        { emoji: "🧠", name: "Когнитивен", desc: "N-back/Stroop · концентрация, памет" },
        { emoji: "🧪", name: "Лабораторен", desc: "HbA1c, ЛДЛ, hsCRP · разчитане на файла с резултатите" },
        { emoji: "🧬", name: "Фенотип", desc: "Инсулин-резистентен, балансиран, въглехидратен" },
        { emoji: "🦠", name: "Микробиом", desc: "30 растения/седм + ферментирали" },
        { emoji: "💪", name: "Телесен", desc: "BMI, BF%, обиколка талия" },
        { emoji: "😴", name: "Сън", desc: "Продължителност, REM, латентност" },
        { emoji: "⚡", name: "Стрес (кортизол)", desc: "Кортизол-натоварване от храненето" },
        { emoji: "🦋", name: "Щитовидна", desc: "TSH, FT4, FT3, anti-TPO · auto-import" },
        { emoji: "🫀", name: "Кардиоваскуларен", desc: "BP, HRV, ApoB · NeuroKit2" },
        { emoji: "🌸", name: "Хормонално-репродуктивен", desc: "Цикъл, фази, STRAW+10" },
        { emoji: "💉", name: "Метаболитно-инсулинов", desc: "HOMA-IR, TG/HDL, ApoB/A1" },
        { emoji: "🔥", name: "Възпалителен", desc: "hsCRP, NLR, анти-възпалителна диета" },
        { emoji: "🏃", name: "Двигателен", desc: "Стъпки, активни мин, VO2 max" },
        { emoji: "🎙️", name: "Психо-емоционален", desc: "Mood, PHQ-9, GAD-7 + гласови биомаркери" },
        { emoji: "🫁", name: "Чернодробно-бъбречен", desc: "ALT, AST, GGT, eGFR" },
        { emoji: "🦷", name: "Дентален (DHI)", desc: "Tooth Protection · перио · бруксизъм · орално-системна връзка" },
        { emoji: "🎗️", name: "BHI (гръдно здраве)", desc: "Естроген · IGF-1 · плътност · ECM · DUTCH metabolites" },
      ],
      connections_title: "Над 110 физиологични връзки между близнаците",
      connections_sub: "Реални клинични механизми (peer-reviewed) свързват близнаците. Стресът блокира щитовидната, недоспиването влошава когнитивната, пародонтитът ускорява атеросклерозата, инсулинът подхранва гръдната биология — всичко се отразява при следващото Ви записване.",
      examples: [
        { from: "⚡ Стрес", to: "🦋 Щитовидна", text: "Хроничен кортизол блокира T4→T3 конверсията (Mancini 2017)" },
        { from: "🔥 Възпаление", to: "🫀 Сърце", text: "Повишеният hsCRP ускорява атеросклерозата (проучване JUPITER)" },
        { from: "🦷 Дентален", to: "🫀 Кардио", text: "Пародонтит ↑ риск от MI и инсулт с ~25% (AHA Statement)" },
        { from: "🦷 Дентален", to: "🎗️ BHI", text: "F. nucleatum в гръдни тумори (Parhi 2020 Nat Commun)" },
        { from: "😴 Сън (OSA)", to: "🫀 Кардио", text: "Sleep apnea → resistant HTN + AFib (Marin 2012 JAMA)" },
        { from: "😴 Сън", to: "🧠 Когниция", text: "Глимфатна система чисти бета-амилоид през N3 (Xie 2013 Science)" },
        { from: "🦠 Микробиом", to: "🎗️ BHI", text: "Estrobolome (β-glucuronidase) → breast estrogen exposure (Kwa 2016 JNCI)" },
        { from: "🧬 Геномика", to: "🎗️ BHI", text: "BRCA1/2 + ATM + PALB2 + CHEK2 → risk modifiers (Kuchenbaecker 2017 JAMA)" },
        { from: "🧬 Геномика", to: "🧠 Когниция", text: "APOE4 удвоява Alzheimer's риска (Corder 1993 Science)" },
        { from: "🏃 Движение", to: "🫀 Кардио", text: "VO2max — най-силен mortality predictor (Mandsager 2018 JAMA)" },
        { from: "🧪 Лаборатории", to: "♀️ Хормонален", text: "Серумни E2/Prog/SHBG → хормонален баланс (Speroff Endocrinology)" },
        { from: "🧪 Лаборатории", to: "🔥 Възпаление", text: "hsCRP > 3 → high CVD risk (AHA/CDC 2003)" },
        { from: "💉 HOMA-IR", to: "🫁 Черен дроб", text: "NAFLD ↔ инсулинова резистентност (бидирекционни)" },
        { from: "🎗️ BHI", to: "⚡ Стрес", text: "Cortisol → mammary GR activation (Volden & Conzen 2013)" },
        { from: "🎗️ BHI", to: "😴 Сън", text: "Melatonin protective; нощни смени = WHO Group 2A (IARC 2010)" },
        { from: "🎗️ BHI", to: "🌸 Хормонален", text: "DUTCH 2-OH/16-OH съотношение — естроген метаболизъм (Lord 2008)" },
        { from: "🎗️ BHI", to: "💉 Метаболитен", text: "Хиперинсулинемия → IGF-1 → mitogenic stimulation (Hankinson 1998)" },
        { from: "🎗️ BHI", to: "🫁 Черен дроб", text: "Hepatic estrogen detox (CYP1A1/1B1) определя клирънса (Tworoger 2014)" },
      ],
      effectiveness_title: "Защо работи",
      effectiveness_items: [
        { stat: "20", label: "близнака следят 20 ключови системи едновременно" },
        { stat: "110+", label: "физиологични връзки с посочен клиничен източник" },
        { stat: "100%", label: "прозрачност — всеки алгоритъм има peer-reviewed цитата" },
        { stat: "секунди", label: "обновяване — при всяко ново хранене или изследване скорът се преизчислява" },
      ],
      cta: "Влезте и вижте Вашите близнаци →",
    },
    story: {
      eyebrow: "Пътят с Nutramilo",
      title: "От данни до резултати — в пет стъпки",
      steps: [
        { title: "Събираме данни", desc: "Хранене, лаборатории, сън, активност, симптоми — без сложност." },
        { title: "Разчитаме организма Ви", desc: "Клинични формули и физиологични правила превръщат данните в смисъл." },
        { title: "Изграждаме мрежата от близнаци", desc: "Живо отражение на тялото Ви, което се допълва всеки ден." },
        { title: "Даваме персонализирани действия", desc: "Конкретни стъпки за днес — не общи съвети." },
        { title: "Проследяваме резултата", desc: "Виждате как всяко решение променя показателите Ви във времето." },
      ],
    },
    how: {
      eyebrow: "Как работи",
      title: "Три стъпки до яснота",
      steps: [
        { icon: Camera, title: "Отбелязвайте данни", desc: "Снимайте храната, кажете го гласово или качете файла с изследванията — стойностите се извличат сами." },
        { icon: LineChart, title: "Вижте разчитането", desc: "Енергия, обмяна, биологична възраст и посока на опорните показатели — на едно място." },
        { icon: Brain, title: "Питайте близнаците", desc: "Разговор с памет за Вашите данни — обяснява защо и какво да промените. На български." },
      ],
      lottie_caption: "Изглежда така в реално време",
    },
    action: {
      eyebrow: "В действие",
      title_patient: "За теб като пациент",
      title_doctor: "За теб като лекар",
      title_clinic: "За Вашата клиника",
      sub_patient: "Вашата физиологична мрежа — 20 свързани близнака, които виждат тялото Ви като една система, а не като отделни изследвания.",
      sub_doctor: "Спестяваш 20+ минути на консултация. SOAP бележки, OCR на лаборатории, OGTT фенотипиране.",
      sub_clinic: "До 100 пациентски слота, white-label отчети, audit log, Enterprise SDK.",
      bullets_patient: [
        "Жива мрежа от близнаци на тялото Ви — обновява се след всяко хранене, нощ и изследване",
        "Обща оценка на близнаците: 20 близнака → едно ясно число за днес",
        "Връзки между близнаците: как напрежението днес ще се отрази на сърцето Ви утре",
        "Гласови биомаркери и HRV — измервай възстановяване от телефона си за 30 секунди",
        "Разговор с памет за Вашите данни — обяснява защо, на разбираем български",
        "Хранителен дневник със снимка на чиния (3 сек анализ) и симулации „какво ако…“",
      ],
      bullets_doctor: ["Разчитане на лабораторни резултати с автоматично нанасяне", "Определяне на обменния тип по обременяване с глюкоза (методология на Станфорд)", "Клинични бележки и изнасяне на документ", "Онко-метаболитни сигнали"],
      bullets_clinic: ["Административно табло за много пациенти", "Разширения по мярка в изчисленията", "Отчети с Вашия знак вместо нашия", "Пакет за вграждане в собствена система"],
      cta_patient: "Започни безплатна оценка",
      cta_doctor: "Вижте възможностите за клиники",
      cta_clinic: "Свържи се с екипа",
    },
    science: {
      eyebrow: "Наука",
      title: "Изграден с лекари. Валидиран с публикации.",
      sub: "Всеки алгоритъм е базиран на peer-reviewed клинични изследвания. Прозрачни формули, отворен код, проследими цитати.",
      placeholder: "Скоро ще се присъедини",
    },
    pricing: {
      eyebrow: "Цени",
      title: "Прозрачни цени, никакви изненади",
      sub: "Цените са съобразени с покупателната способност по държави. Започнете безплатно, без платежна карта.",
      cta: "Вижте плановете",
    },
    citations: {
      eyebrow: "РЕЦЕНЗИРАНА НАУКА",
      title: "Изграден върху наука, не хипотези",
      sub: "Моделът на инсулиногенния бюджет на Nutramilo е базиран на реални клинични изследвания за инсулинови индекси, метаболитен синдром и хранителна ендокринология.",
      cta: "Вижте всички публикации",
      library_cta: "Научна библиотека",
    },
    sticky: { trial: "30 дни безплатно", cta: "Вижте плановете →" },
    about: {
      heading: "Какво е Nutramilo?",
      body: "Nutramilo е образователна платформа за персонализирано метаболитно здраве. Тя подрежда Вашите лабораторни изследвания, телесни показатели и хранителни навици в мрежа от 20 близнака за отделните системи на организма, свързани с над 110 физиологични зависимости. Всяка оценка показва от какво е съставена, а платформата изнася подробен доклад, който да обсъдите с Вашия лекар.",
      note: "Не е медицинско изделие. Не заменя лекар.",
      privacy: "Как ползваме данните",
    },
    twins_net: {
      eyebrow: "Физиологична архитектура",
      title_prefix: "близнака за отделни системи. Едно общо равновесие.",
      sub: "Всеки близнак отговаря за точно определена система и получава само съответните данни. Заедно образуват жива мрежа, в която нито един показател не се губи и не се брои двукратно.",
      filter_all: "Всички близнаци",
      filter_organs: "Обмяна и органи",
      filter_lifestyle: "Начин на живот",
      filter_clinical: "Клинични и ендокринни",
    },
    circles: {
      eyebrow: "Системна биология",
      title: "Над 110 физиологични връзки и откриване на порочни кръгове",
      sub: "Напрежението не остава в нервната система — то потиска превръщането на хормоните на щитовидната жлеза. Кратката нощ мени отговора към инсулин още на следващия ден. Мрежата подрежда тези зависимости и показва кой кръг гори днес и къде е лостът.",
    },
    capabilities: {
      eyebrow: "Как работи в ежедневието",
      title: "Въведеното на едно място стига до всеки близнак, който го засяга",
      sub: "Няма раздел, който да съществува сам за себе си. Резултатът от изследване, едно хранене или едно отбелязване „днес“ променят оценките на близнаците, които зависят от тях — и всяка промяна носи причината си.",
      note_title: "Без скрити изчисления",
      note_body: "Всяка оценка се отваря и показва от какво е съставена: показателите, които близнакът оценява, показателите, които ползва като контекст, и разделите, които са я намалили — с причината за всяко намаление. Платформата подрежда собствените Ви данни и има образователна цел; тя не поставя диагноза и не заменя лекарски преглед.",
      cta_primary: "Създайте профил",
      cta_secondary: "Как работи мрежата",
    },
    final: {
      title: "Готов да разбереш тялото си?",
      sub: "Започнете с кратка безплатна оценка. Без платежна карта.",
      cta: "Започни сега",
      disclaimer: "Nutramilo не е медицинско изделие по смисъла на MDR 2017/745. Не замества медицинска консултация.",
    },
  },
  en: {
    nav: { science: "Science", pricing: "Pricing", assessment: "Free assessment", guide: "Guide", faq: "FAQ", compare: "Compare", signin: "Sign in", signup: "Sign Up", dashboard: "My Health", how: "How it works", smart: "Smart Eating", about: "About us", consultants: "Consultants", tools: "Tools" },
    sources: {
      eyebrow: "Our sources",
      title: "Data from leading scientific databases",
      sub: "Every recommendation in Nutramilo is grounded in trusted, publicly accessible, peer-reviewed sources. We don't invent — we verify.",
      items: [
        { name: "DrugBank", desc: "Drug–nutrient interactions for 41+ medications" },
        { name: "PubMed (NCBI)", desc: "30M+ medical publications — PMID, abstracts, authors, citations" },
        { name: "Semantic Scholar", desc: "200M+ scientific articles with citations and metadata — Graph API" },
        { name: "PubChem (NIH)", desc: "Chemical information for supplements and ingredients" },
        { name: "Open Food Facts", desc: "Barcode data for thousands of supermarket products" },
        { name: "USDA FoodData", desc: "Nutritional composition for 23,000+ foods from USDA" },
        { name: "ClinVar (NCBI)", desc: "Genetic variants and clinical significance" },
        { name: "Open Targets", desc: "Gene–disease associations and drug targets" },
        { name: "OpenAI GPT-5.2", desc: "AI analysis via four specialised agents" },
        { name: "Edamam", desc: "Recipes and nutritional analysis" },
      ],
    },
    hero: {
      eyebrow: "No payment card · 30 days free",
      headline_1: "See what every meal",
      headline_2: "actually does to your body.",
      sub: "Log the plate — with a photo, your voice or by hand. Within a minute you see how much it loads the body, what exactly drives it up and what to change at the next meal.",
      cta_primary: "Try it with one meal",
      cta_secondary: "How it works",
      trust_1: "An answer in 1 minute",
      trust_2: "No sign-up for the first try",
      trust_3: "Your data stays yours",
      proof: "The answer rests on 20 physiological twins and more than 110 connections between them — that is why it is more precise than plain calorie counting.",
      md_badge: "Educational tool · not a medical device · MDR 2017/745",
      card_kicker: "How today's meal landed",
      card_verdict: "Today the food is light on the body",
      card_expl: "You used only 24% of your daily reference. Keep going like this and this is a day that helps.",
      card_nis: "Calm insulin response — the food is absorbed smoothly.",
      card_cta: "See today",
      card_score: "24 of 100",
      card_score_label: "load (DIL)",
      card_caption: "This is exactly what a user sees after the first logged meal.",
    },
    steps: {
      label: "Three steps",
      title: "From plate to a clear answer",
      items: [
        { title: "Log the meal", desc: "A photo of the plate, your voice or a search by name. Under a minute." },
        { title: "See the answer", desc: "One clear result in words: how much it loads, what drives it and what to change." },
        { title: "Ask why", desc: "Talk to the twins about hormones, the liver, sleep — on top of your own data." },
      ],
      why_label: "Why the answer is precise",
      why_title: "Behind the simple answer sits the whole architecture",
      why: [
        "20 physiological twins — metabolism, hormones, liver, kidneys, sleep, breast health and more.",
        "More than 110 connections between them — one entered value reaches every twin it affects.",
        "Vicious-circle detection — when two problems feed each other.",
        "Sources and thresholds from scientific databases, with a citation for every threshold.",
      ],
    },
    testimonials: [
      { id: "maria", name: "Maria, 47", text: "In 30 days: DIL 68 → 41. I wake up without fatigue." },
      { id: "petar", name: "Petar, 52", text: "In 30 days: waist −4 cm. I stopped the afternoon sweets." },
      { id: "elena", name: "Elena, 38", text: "In 30 days: I understood why I get hungry at 4 pm. Swapped 2 snacks." },
    ],
    testimonial_badge: "30 days",
    audience_tabs: { title: "Who are you?", patient: "I'm a patient", doctor: "I'm a doctor", clinic: "I'm a clinic" },
    platform: {
      eyebrow: "The platform",
      title: "Three intelligence layers. One complete picture.",
      sub: "Every feature feeds your digital health twin — from nutrition to labs and organ-specific models.",
      learn: "Learn more",
      layers: [
        { id: "health", title: "Health Intelligence", tagline: "Understand the full picture of your health — live dashboard, AI analyses, insights and forecasts." },
        { id: "nutrition", title: "Nutrition Intelligence", tagline: "Discover which foods work best for your body — and what every meal does to you." },
        { id: "precision", title: "Precision Health", tagline: "Labs, screenings and organ-specific models — precision beyond nutrition." },
      ],
      features: [
        {
          id: "health-intelligence",
          layer: "health",
          title: "Health Intelligence",
          desc: "Your personal metabolic board: BMI, VAIS, visceral index, biological age, weekly direction and insights.",
          learnMore: "Health Intelligence unifies all your data into one live board — body composition, metabolic markers, diary trends, latest labs and AI insights. See how your biological age changes, what affects you most (Top Driver), and get personalised recommendations for the coming week.",
          linkBg: "See the dashboard",
        },
        {
          id: "nutrition-lab",
          layer: "nutrition",
          title: "Nutrition Intelligence Lab",
          desc: "Discover which foods work best for your body.",
          learnMore: "Pick your conditions and goals — Nutrition Lab ranks foods by your personal food match, and with one click AI builds a 7-day menu from exactly those foods. Behind it: 20,000+ scored foods, 20+ health conditions, scientific metabolic pathways (KEGG), and the Nutramilo indices (Insulinogenic Budget, Insulin Response, COL3 and more).",
          linkBg: "Open Nutrition Lab",
        },
        {
          id: "diary",
          layer: "nutrition",
          title: "Food diary",
          desc: "Photograph your plate → AI recognises ingredients in 3 seconds and computes the indices automatically.",
          learnMore: "For every meal, see how it affects your insulin level and how much load it adds to your day. No CGM, no blood tests — just seconds.",
          linkBg: "See the diary",
        },
        {
          id: "analyses",
          layer: "health",
          title: "Analyses",
          desc: "See your body composition, energy profile, and how you change over time.",
          learnMore: "A complete view of body composition, metabolic health, and biological age. No scanners — just from the lab tests and measurements you already have. Results are tracked over time so you can see real progress.",
          linkBg: "See analyses",
        },
        {
          id: "ai",
          layer: "health",
          title: "Ask AI",
          desc: "A conversation that remembers your data, step by step.",
          learnMore: "The conversation remembers your previous questions, lab results and dietary patterns. Ask things like 'Why isn't my HOMA-IR coming down?' or 'When should I eat after training?' — get a concrete answer based on your data.",
          linkBg: "Chat with AI",
        },
        {
          id: "labs",
          layer: "precision",
          title: "Lab OCR",
          desc: "Upload a PDF — automatic extraction of HOMA-IR, lipids, hsCRP, vitamins, ALT.",
          learnMore: "Supports Bulgarian labs (СМДЛ, Цибалаб, Бодимед, etc.) plus international ones. OCR extracts 30+ markers and writes them into your profile automatically. Sensitive markers always get a 'verify' comment.",
          linkBg: "Try it",
        },
        {
          id: "screen",
          layer: "precision",
          title: "AI screenings",
          desc: "Skin, teeth, breast — AI-assisted visual checks. Not a replacement for your doctor.",
          learnMore: "Three separate modules: skin screening for malignant changes (dermatology), visual dental assessment (periodontitis, caries), and breast self-examination helper. All carry clear disclaimers and direct you to a specialist.",
          linkBg: "See screenings",
          subLinks: [
            { label: "Skin", to: "/skin-screening" },
            { label: "Teeth", to: "/dental-screening" },
            { label: "Breast", to: "/health-intelligence/breast" },
          ],
        },
      ],
    },
    twin_core: {
      eyebrow: "The core of the platform",
      title: "How is the twin network driven?",
      sub: "Everything you enter flows into a living reflection of your body. Every value reaches the twins that depend on it.",
      inputs: ["Nutrition", "Labs", "Sleep", "Movement", "Complaints"],
      ai_label: "Physiological rules and clinical formulas",
      twin_label: "A network of 20 twins",
      out_label: "A score with its source and the next step",
      note: "The network updates with every meal, measurement or lab result — so the score reflects your state today, not your state a month ago.",
      cta: "See the network in action",
    },
    twins: {
      eyebrow: "The 20 digital twins",
      title: "Not one — a whole team of twins for you",
      sub: "Each twin monitors a different body system. Together they form a complete health picture — with physiological connections between them that recompute on every new entry.",
      list: [
        { emoji: "🍽️", name: "Nutritional", desc: "DIL, carbs, fiber, omega balance" },
        { emoji: "🧠", name: "Cognitive", desc: "N-back/Stroop · focus, working memory" },
        { emoji: "🧪", name: "Lab", desc: "HbA1c, LDL, hsCRP · PDF Vision OCR" },
        { emoji: "🧬", name: "Phenotype", desc: "Insulin-resistant, balanced, carb-metabolizer" },
        { emoji: "🦠", name: "Microbiome", desc: "30 plants/week + fermented foods" },
        { emoji: "💪", name: "Body composition", desc: "BMI, BF%, waist circumference" },
        { emoji: "😴", name: "Sleep", desc: "Duration, REM, latency" },
        { emoji: "⚡", name: "Stress (cortisol)", desc: "Cortisol load from food + timing" },
        { emoji: "🦋", name: "Thyroid", desc: "TSH, FT4, FT3, anti-TPO · auto-import" },
        { emoji: "🫀", name: "Cardiovascular", desc: "BP, HRV, ApoB · NeuroKit2" },
        { emoji: "🌸", name: "Hormonal-reproductive", desc: "Cycle, phases, STRAW+10" },
        { emoji: "💉", name: "Metabolic-insulin", desc: "HOMA-IR, TG/HDL, ApoB/A1" },
        { emoji: "🔥", name: "Inflammation", desc: "hsCRP, NLR, anti-inflammatory diet" },
        { emoji: "🏃", name: "Movement", desc: "Steps, active min, VO2 max" },
        { emoji: "🎙️", name: "Mental-emotional", desc: "Mood, PHQ-9, GAD-7 + voice biomarkers" },
        { emoji: "🫁", name: "Liver-kidney", desc: "ALT, AST, GGT, eGFR" },
        { emoji: "🦷", name: "Dental (DHI)", desc: "Tooth Protection · perio · bruxism · oral-systemic link" },
        { emoji: "🎗️", name: "BHI (breast health)", desc: "Estrogen · IGF-1 · density · ECM · DUTCH metabolites" },
      ],
      connections_title: "More than 110 physiological connections between twins",
      connections_sub: "Real clinical mechanisms (peer-reviewed) link the twins. Stress blocks the thyroid, lack of sleep impairs cognition, periodontitis accelerates atherosclerosis, insulin fuels breast biology — all reflected on your next entry.",
      examples: [
        { from: "⚡ Stress", to: "🦋 Thyroid", text: "Chronic cortisol blocks T4→T3 conversion (Mancini 2017)" },
        { from: "🔥 Inflammation", to: "🫀 Cardio", text: "hsCRP↑ accelerates atherosclerosis (JUPITER trial)" },
        { from: "🦷 Dental", to: "🫀 Cardio", text: "Periodontitis raises MI & stroke risk by ~25% (AHA Statement)" },
        { from: "🦷 Dental", to: "💉 Metabolic", text: "Perio ↔ HbA1c — bidirectional link (Preshaw 2012)" },
        { from: "😴 Sleep", to: "🧠 Cognition", text: "Glymphatic system clears beta-amyloid in N3 sleep (Xie 2013 Science)" },
        { from: "💉 HOMA-IR", to: "🫁 Liver", text: "NAFLD ↔ insulin resistance are bidirectional" },
        { from: "🎗️ BHI", to: "🌸 Hormonal", text: "DUTCH 2-OH/16-OH ratio reflects estrogen metabolism (Lord 2008)" },
        { from: "🎗️ BHI", to: "💉 Metabolic", text: "Hyperinsulinemia → IGF-1 → mitogenic effect on breast tissue (Hankinson 1998)" },
        { from: "🎗️ BHI", to: "🫁 Liver", text: "Phase-I/II hepatic detox governs estrogen clearance (Tworoger 2014)" },
      ],
      effectiveness_title: "Why it works",
      effectiveness_items: [
        { stat: "20", label: "twins monitor 20 key systems simultaneously" },
        { stat: "110+", label: "physiological connections with stated clinical sources" },
        { stat: "100%", label: "transparency — every algorithm has a peer-reviewed citation" },
        { stat: "seconds", label: "recompute — scores update after each new meal or lab entry" },
      ],
      cta: "Sign in & see your twins →",
    },
    story: {
      eyebrow: "The Nutramilo journey",
      title: "From data to results — in five steps",
      steps: [
        { title: "We collect data", desc: "Nutrition, labs, sleep, activity, symptoms — without complexity." },
        { title: "We understand your body", desc: "Scientific formulas and AI models turn data into meaning." },
        { title: "We build your digital health twin", desc: "A living reflection of your body that learns from you every day." },
        { title: "We give personalised actions", desc: "Concrete steps for today — not generic advice." },
        { title: "We track the results", desc: "See how every decision changes your markers over time." },
      ],
    },
    how: {
      eyebrow: "How it works",
      title: "Three steps to clarity",
      steps: [
        { icon: Camera, title: "Log your data", desc: "Photograph meals, voice-log, or upload lab PDFs — automatic value extraction." },
        { icon: LineChart, title: "See your analyses", desc: "Energy, metabolism, biological age, trends in key markers — all in one place." },
        { icon: Brain, title: "Ask the twins", desc: "A conversation that remembers your data — explains why and what to change." },
      ],
      lottie_caption: "This is what it looks like in real time",
    },
    action: {
      eyebrow: "In action",
      title_patient: "For you as a patient",
      title_doctor: "For you as a doctor",
      title_clinic: "For your clinic",
      sub_patient: "Transparency into what's happening in your body — no jargon, no diagnoses.",
      sub_doctor: "Save 20+ minutes per consultation. SOAP notes, lab OCR, OGTT phenotyping.",
      sub_clinic: "Up to 100 patient slots, white-label reports, audit log, Enterprise SDK.",
      bullets_patient: ["A conversation that remembers your data", "Food log with AI plate photo (3 sec analysis)", "See how each meal affects your energy", "Simulations: what if I sleep more? Eat fewer carbs?", "Biological age and metabolic fitness", "Recipes optimised for stable energy"],
      bullets_doctor: ["Lab OCR → automatic data entry", "OGTT phenotyping (Stanford methodology)", "SOAP notes + PDF export", "Onco-metabolic signals"],
      bullets_clinic: ["Admin board for many patients", "Tailored extensions to the calculations", "Reports carrying your mark instead of ours", "Package for embedding into your own system"],
      cta_patient: "Start free assessment", cta_doctor: "See Clinic features", cta_clinic: "Contact the team",
    },
    science: {
      eyebrow: "Science",
      title: "Built with doctors. Validated by publications.",
      sub: "Every algorithm is grounded in peer-reviewed clinical research. Transparent formulas, open source, traceable citations.",
      placeholder: "Joining soon",
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Transparent pricing, no surprises",
      sub: "Prices adjusted to purchasing power by country. Start free, no payment card.",
      cta: "View plans",
    },
    citations: {
      eyebrow: "PEER-REVIEWED SCIENCE",
      title: "Built on science, not assumptions",
      sub: "Nutramilo's insulinogenic budget model is based on real clinical research on insulin indices, metabolic syndrome, and nutritional endocrinology.",
      cta: "View all publications",
      library_cta: "Scientific library",
    },
    sticky: { trial: "Free 30-day trial", cta: "View plans →" },
    about: {
      heading: "What is Nutramilo?",
      body: "Nutramilo is an educational platform for personalised metabolic health. From your labs, anthropometry and food diary it computes an individual plan, tracks 20 organ-level 'Digital Twins' and activates the relevant KEGG pathways. Every recommendation carries a PMID/KEGG citation from validated sources (CTD·NIEHS, Phenol-Explorer·INRA, Cochrane) and exports as a clinician-ready PDF for your doctor.",
      note: "Not a medical device. Does not replace a doctor.",
      privacy: "How we use your data",
    },
    twins_net: {
      eyebrow: "Physiological architecture",
      title_prefix: "twins for separate systems. One shared balance.",
      sub: "Each twin is responsible for one precisely defined system and receives only the data that belongs to it. Together they form a living network in which no marker is lost and none is counted twice.",
      filter_all: "All twins",
      filter_organs: "Metabolism and organs",
      filter_lifestyle: "Daily life",
      filter_clinical: "Clinical and endocrine",
    },
    circles: {
      eyebrow: "Systems biology",
      title: "More than 110 physiological connections and vicious-circle detection",
      sub: "Stress does not stay in the nervous system — it suppresses the conversion of thyroid hormone. A short night changes the insulin response the very next day. The network orders these dependencies and shows which circle is burning today and where the lever is.",
    },
    capabilities: {
      eyebrow: "How it works day to day",
      title: "What you enter in one place reaches every twin it concerns",
      sub: "No section exists on its own. A lab result, a single meal or one \u201etoday\u201c entry change the scores of the twins that depend on them — and every change carries its reason.",
      note_title: "No hidden arithmetic",
      note_body: "Every score opens up and states what it is built from: the markers the twin scores, the markers it uses as context, and the sections that reduced it — with the reason for each reduction. The platform orders your own data and serves an educational purpose; it does not make a diagnosis and does not replace a medical examination.",
      cta_primary: "Create an account",
      cta_secondary: "How the network works",
    },
    final: {
      title: "Ready to understand your body?",
      sub: "Start with a free 60-second assessment. No credit card.",
      cta: "Get started",
      disclaimer: "Nutramilo is not a medical device under MDR 2017/745. Does not replace medical consultation.",
    },
  },
  es: {
    nav: { science: "Ciencia", pricing: "Precios", assessment: "Evaluación gratuita", guide: "Guía", faq: "FAQ", compare: "Comparar", signin: "Iniciar sesión", signup: "Registrarse", dashboard: "Mi Salud", how: "Cómo funciona", smart: "Alimentación Inteligente", about: "Quiénes somos", consultants: "Consultores", tools: "Herramientas" },
    sources: {
      eyebrow: "Nuestras fuentes",
      title: "Datos de las principales bases de datos científicas",
      sub: "Cada recomendación en Nutramilo está respaldada por fuentes fiables, accesibles públicamente y revisadas por pares. No inventamos — verificamos.",
      items: [
        { name: "DrugBank", desc: "Interacciones fármaco-nutriente para más de 41 medicamentos" },
        { name: "PubMed (NCBI)", desc: "Más de 30 millones de publicaciones médicas — PMID, resúmenes, autores, citas" },
        { name: "Semantic Scholar", desc: "Más de 200 millones de artículos científicos con citas y metadatos — Graph API" },
        { name: "PubChem (NIH)", desc: "Información química sobre suplementos e ingredientes" },
        { name: "Open Food Facts", desc: "Datos de código de barras de miles de productos de supermercado" },
        { name: "USDA FoodData", desc: "Composición nutricional de más de 23.000 alimentos del USDA" },
        { name: "ClinVar (NCBI)", desc: "Variantes genéticas y significado clínico" },
        { name: "Open Targets", desc: "Asociaciones gen-enfermedad y dianas farmacológicas" },
        { name: "OpenAI GPT-5.2", desc: "Análisis AI mediante cuatro agentes especializados" },
        { name: "Edamam", desc: "Recetas y análisis nutricional" },
      ],
    },
    hero: {
      eyebrow: "Sin tarjeta de pago · 30 días gratis",
      headline_1: "Descubra qué hace",
      headline_2: "cada comida en su cuerpo.",
      sub: "Registre el plato — con una foto, con la voz o a mano. En un minuto ve cuánto carga al cuerpo, qué lo eleva exactamente y qué cambiar en la próxima comida.",
      cta_primary: "Pruébelo con una comida",
      cta_secondary: "Cómo funciona",
      trust_1: "Respuesta en 1 minuto",
      trust_2: "Sin registro para el primer intento",
      trust_3: "Sus datos siguen siendo suyos",
      proof: "La respuesta se apoya en 20 gemelos fisiológicos y más de 110 conexiones entre ellos — por eso es más precisa que contar calorías.",
      md_badge: "Herramienta educativa · no es un dispositivo médico · MDR 2017/745",
      card_kicker: "Cómo sentó la comida de hoy",
      card_verdict: "Hoy la comida es ligera para el cuerpo",
      card_expl: "Ha usado solo el 24% de su referencia diaria. Si sigue así, este es un día que ayuda.",
      card_nis: "Respuesta de insulina tranquila — la comida se asimila con suavidad.",
      card_cta: "Ver el día de hoy",
      card_score: "24 de 100",
      card_score_label: "carga (DIL)",
      card_caption: "Esto es exactamente lo que ve el usuario tras la primera comida registrada.",
    },
    steps: {
      label: "Tres pasos",
      title: "Del plato a una respuesta clara",
      items: [
        { title: "Registre la comida", desc: "Una foto del plato, la voz o una búsqueda por nombre. Menos de un minuto." },
        { title: "Vea la respuesta", desc: "Un resultado claro en palabras: cuánto carga, qué lo eleva y qué cambiar." },
        { title: "Pregunte por qué", desc: "Hable con los gemelos sobre hormonas, hígado o sueño — sobre sus propios datos." },
      ],
      why_label: "Por qué la respuesta es precisa",
      why_title: "Detrás de la respuesta simple está toda la arquitectura",
      why: [
        "20 gemelos fisiológicos — metabolismo, hormonas, hígado, riñones, sueño, salud mamaria y más.",
        "Más de 110 conexiones entre ellos — un valor introducido llega a cada gemelo al que afecta.",
        "Detección de círculos viciosos — cuando dos problemas se alimentan entre sí.",
        "Fuentes y umbrales de bases científicas, con la cita de cada umbral.",
      ],
    },
    testimonials: [
      { id: "maria", name: "María, 47", text: "En 30 días: DIL 68 → 41. Me despierto sin cansancio." },
      { id: "petar", name: "Petar, 52", text: "En 30 días: cintura −4 cm. Dejé el dulce de la tarde." },
      { id: "elena", name: "Elena, 38", text: "En 30 días: entendí por qué tengo hambre a las 16 h. Cambié 2 meriendas." },
    ],
    testimonial_badge: "30 días",
    audience_tabs: { title: "¿Quién eres?", patient: "Soy paciente", doctor: "Soy médico", clinic: "Soy una clínica" },
    platform: {
      eyebrow: "La plataforma",
      title: "Tres capas de inteligencia. Una imagen completa.",
      sub: "Cada función alimenta tu gemelo digital de salud — desde la nutrición hasta las analíticas y los modelos específicos por órgano.",
      learn: "Saber más",
      layers: [
        { id: "health", title: "Inteligencia de Salud", tagline: "Comprende el panorama completo de tu salud — panel en directo, análisis AI, insights y previsiones." },
        { id: "nutrition", title: "Inteligencia Nutricional", tagline: "Descubre qué alimentos funcionan mejor para tu cuerpo — y qué hace cada comida en ti." },
        { id: "precision", title: "Salud de Precisión", tagline: "Analíticas, cribados y modelos específicos por órgano — precisión más allá de la nutrición." },
      ],
      features: [
        {
          id: "health-intelligence",
          layer: "health",
          title: "Inteligencia de Salud",
          desc: "Tu panel metabólico personal: BMI, VAIS, índice visceral, edad biológica, tendencias semanales e insights AI.",
          learnMore: "La Inteligencia de Salud unifica todos tus datos en un panel en directo — composición corporal, marcadores metabólicos, tendencias del diario, últimas analíticas e insights AI. Observa cómo cambia tu edad biológica, qué te afecta más (Top Driver) y recibe recomendaciones personalizadas para la próxima semana.",
          linkBg: "Ver el panel",
        },
        {
          id: "nutrition-lab",
          layer: "nutrition",
          title: "Laboratorio de Inteligencia Nutricional",
          desc: "Descubre qué alimentos funcionan mejor para tu cuerpo.",
          learnMore: "Elige tus condiciones y objetivos — Nutrition Lab clasifica los alimentos según su coincidencia personal personal, y con un clic AI elabora un menú de 7 días a partir exactamente de esos alimentos. Detrás: más de 20.000 alimentos puntuados, 20+ condiciones de salud, rutas metabólicas científicas (KEGG) y los índices Nutramilo (Insulinogenic Budget, Insulin Response, COL3 y más).",
          linkBg: "Abrir Nutrition Lab",
        },
        {
          id: "diary",
          layer: "nutrition",
          title: "Diario alimentario",
          desc: "Fotografía tu plato → AI reconoce los ingredientes en 3 segundos y calcula los índices automáticamente.",
          learnMore: "Para cada comida, observa cómo afecta a tu nivel de insulina y cuánta carga añade a tu día. Sin CGM, sin análisis de sangre — solo segundos.",
          linkBg: "Ver el diario",
        },
        {
          id: "analyses",
          layer: "health",
          title: "Análisis",
          desc: "Consulta tu composición corporal, perfil energético y cómo cambias con el tiempo.",
          learnMore: "Una visión completa de la composición corporal, la salud metabólica y la edad biológica. Sin escáneres — solo a partir de las analíticas y mediciones que ya tienes. Los resultados se registran a lo largo del tiempo para que puedas ver el progreso real.",
          linkBg: "Ver análisis",
        },
        {
          id: "ai",
          layer: "health",
          title: "Preguntar a AI",
          desc: "Coach AI personal con memoria de tus datos. Conversación multiturn.",
          learnMore: "El coach AI recuerda tus preguntas anteriores, resultados de analíticas y patrones dietéticos. Pregunta cosas como '¿Por qué no baja mi HOMA-IR?' o '¿Cuándo debería comer después del entrenamiento?' — obtén una respuesta concreta basada en tus datos.",
          linkBg: "Chatear con AI",
        },
        {
          id: "labs",
          layer: "precision",
          title: "Lab OCR",
          desc: "Sube un PDF — extracción automática de HOMA-IR, lípidos, hsCRP, vitaminas, ALT.",
          learnMore: "Compatible con laboratorios búlgaros (СМДЛ, Цибалаб, Бодимед, etc.) e internacionales. OCR extrae más de 30 marcadores y los escribe automáticamente en tu perfil. Los marcadores sensibles siempre reciben un comentario de 'verificar'.",
          linkBg: "Probarlo",
        },
        {
          id: "screen",
          layer: "precision",
          title: "Cribados AI",
          desc: "Piel, dientes, mama — revisiones visuales asistidas por AI. No sustituyen a tu médico.",
          learnMore: "Tres módulos independientes: cribado de piel para cambios malignos (dermatología), valoración dental visual (periodontitis, caries) y asistente de autoexploración mamaria. Todos incluyen avisos legales claros y te remiten a un especialista.",
          linkBg: "Ver cribados",
          subLinks: [
            { label: "Piel", to: "/skin-screening" },
            { label: "Dientes", to: "/dental-screening" },
            { label: "Mama", to: "/health-intelligence/breast" },
          ],
        },
      ],
    },
    twin_core: {
      eyebrow: "El núcleo de la plataforma",
      title: "¿Cómo se pone en marcha la red de gemelos?",
      sub: "Todo lo que introduce fluye hacia un reflejo vivo de su organismo. Cada valor llega a los gemelos que dependen de él.",
      inputs: ["Nutrición", "Analíticas", "Sueño", "Movimiento", "Molestias"],
      ai_label: "Reglas fisiológicas y fórmulas clínicas",
      twin_label: "Una red de 20 gemelos",
      out_label: "Una puntuación con su fuente y el siguiente paso",
      note: "La red se actualiza con cada comida, medición o analítica — por eso la puntuación refleja su estado de hoy y no el de hace un mes.",
      cta: "Vea la red en acción",
    },
    twins: {
      eyebrow: "Los 20 gemelos digitales",
      title: "No uno — todo un equipo de gemelos para ti",
      sub: "Cada gemelo monitoriza un sistema corporal diferente. Juntos forman una imagen de salud completa — con conexiones fisiológicas entre ellos que se recalculan con cada nueva entrada.",
      list: [
        { emoji: "🍽️", name: "Nutricional", desc: "DIL, carbohidratos, fibra, equilibrio omega" },
        { emoji: "🧠", name: "Cognitivo", desc: "N-back/Stroop · concentración, memoria de trabajo" },
        { emoji: "🧪", name: "Analítico", desc: "HbA1c, LDL, hsCRP · PDF Vision OCR" },
        { emoji: "🧬", name: "Fenotipo", desc: "Resistente a la insulina, equilibrado, metabolizador de carbohidratos" },
        { emoji: "🦠", name: "Microbioma", desc: "30 plantas/semana + alimentos fermentados" },
        { emoji: "💪", name: "Composición corporal", desc: "BMI, BF%, perímetro de cintura" },
        { emoji: "😴", name: "Sueño", desc: "Duración, REM, latencia" },
        { emoji: "⚡", name: "Estrés (cortisol)", desc: "Carga de cortisol por alimentos + timing" },
        { emoji: "🦋", name: "Tiroides", desc: "TSH, FT4, FT3, anti-TPO · importación automática" },
        { emoji: "🫀", name: "Cardiovascular", desc: "BP, HRV, ApoB · NeuroKit2" },
        { emoji: "🌸", name: "Hormonal-reproductivo", desc: "Ciclo, fases, STRAW+10" },
        { emoji: "💉", name: "Metabólico-insulínico", desc: "HOMA-IR, TG/HDL, ApoB/A1" },
        { emoji: "🔥", name: "Inflamación", desc: "hsCRP, NLR, dieta antiinflamatoria" },
        { emoji: "🏃", name: "Movimiento", desc: "Pasos, minutos activos, VO2 max" },
        { emoji: "🎙️", name: "Mental-emocional", desc: "Estado de ánimo, PHQ-9, GAD-7 + biomarcadores de voz" },
        { emoji: "🫁", name: "Hígado-riñón", desc: "ALT, AST, GGT, eGFR" },
        { emoji: "🦷", name: "Dental (DHI)", desc: "Protección dental · perio · bruxismo · vínculo oral-sistémico" },
        { emoji: "🎗️", name: "BHI (salud mamaria)", desc: "Estrógeno · IGF-1 · densidad · ECM · metabolitos DUTCH" },
      ],
      connections_title: "Más de 110 conexiones fisiológicas entre gemelos",
      connections_sub: "Mecanismos clínicos reales (revisados por pares) vinculan los gemelos. El estrés bloquea la tiroides, la falta de sueño deteriora la cognición, la periodontitis acelera la aterosclerosis, la insulina alimenta la biología mamaria — todo reflejado en tu próxima entrada.",
      examples: [
        { from: "⚡ Estrés", to: "🦋 Tiroides", text: "El cortisol crónico bloquea la conversión T4→T3 (Mancini 2017)" },
        { from: "🔥 Inflamación", to: "🫀 Cardio", text: "hsCRP↑ acelera la aterosclerosis (ensayo JUPITER)" },
        { from: "🦷 Dental", to: "🫀 Cardio", text: "La periodontitis aumenta el riesgo de IM e ictus en ~25% (AHA Statement)" },
        { from: "🦷 Dental", to: "💉 Metabólico", text: "Perio ↔ HbA1c — vínculo bidireccional (Preshaw 2012)" },
        { from: "😴 Sueño", to: "🧠 Cognición", text: "El sistema glinfático elimina beta-amiloide en el sueño N3 (Xie 2013 Science)" },
        { from: "💉 HOMA-IR", to: "🫁 Hígado", text: "NAFLD ↔ resistencia a la insulina son bidireccionales" },
        { from: "🎗️ BHI", to: "🌸 Hormonal", text: "Ratio DUTCH 2-OH/16-OH refleja el metabolismo del estrógeno (Lord 2008)" },
        { from: "🎗️ BHI", to: "💉 Metabólico", text: "Hiperinsulinemia → IGF-1 → estímulo mitogénico en tejido mamario (Hankinson 1998)" },
        { from: "🎗️ BHI", to: "🫁 Hígado", text: "La detoxificación hepática Fase-I/II gobierna el aclaramiento de estrógenos (Tworoger 2014)" },
      ],
      effectiveness_title: "Por qué funciona",
      effectiveness_items: [
        { stat: "20", label: "gemelos monitorizan 20 sistemas clave simultáneamente" },
        { stat: "110+", label: "conexiones fisiológicas con fuentes clínicas declaradas" },
        { stat: "100%", label: "transparencia — cada algoritmo tiene una cita revisada por pares" },
        { stat: "segundos", label: "recálculo — las puntuaciones se actualizan tras cada nueva comida o análisis" },
      ],
      cta: "Iniciar sesión y ver tus gemelos →",
    },
    story: {
      eyebrow: "El camino de Nutramilo",
      title: "De los datos a los resultados — en cinco pasos",
      steps: [
        { title: "Recogemos datos", desc: "Nutrición, analíticas, sueño, actividad, síntomas — sin complicaciones." },
        { title: "Entendemos tu cuerpo", desc: "Fórmulas científicas y modelos AI convierten los datos en significado." },
        { title: "Construimos tu gemelo digital de salud", desc: "Un reflejo vivo de tu cuerpo que aprende de ti cada día." },
        { title: "Damos acciones personalizadas", desc: "Pasos concretos para hoy — no consejos genéricos." },
        { title: "Hacemos seguimiento de los resultados", desc: "Observa cómo cada decisión cambia tus marcadores con el tiempo." },
      ],
    },
    how: {
      eyebrow: "Cómo funciona",
      title: "Tres pasos hacia la claridad",
      steps: [
        { icon: Camera, title: "Registra tus datos", desc: "Fotografía comidas, registra por voz o sube PDFs de analíticas — extracción automática de valores." },
        { icon: LineChart, title: "Consulta tus análisis", desc: "Energía, metabolismo, edad biológica, tendencias en marcadores clave — todo en un solo lugar." },
        { icon: Brain, title: "Pregunta a AI", desc: "Coach AI personal con memoria de tus datos — explica por qué y qué cambiar." },
      ],
      lottie_caption: "Así es como se ve en tiempo real",
    },
    action: {
      eyebrow: "En acción",
      title_patient: "Para ti como paciente",
      title_doctor: "Para ti como médico",
      title_clinic: "Para tu clínica",
      sub_patient: "Transparencia sobre lo que ocurre en tu cuerpo — sin jerga, sin diagnósticos.",
      sub_doctor: "Ahorra más de 20 minutos por consulta. Notas SOAP, Lab OCR, fenotipado OGTT.",
      sub_clinic: "Hasta 100 fichas de pacientes, informes white-label, registro de auditoría, Enterprise SDK.",
      bullets_patient: ["Coach AI personal con memoria de tus datos", "Registro alimentario con foto de plato AI (análisis en 3 seg)", "Observa cómo afecta cada comida a tu energía", "Simulaciones: ¿qué pasa si duermo más? ¿Como menos carbohidratos?", "Edad biológica y forma metabólica", "Recetas optimizadas para una energía estable"],
      bullets_doctor: ["Lab OCR → entrada automática de datos", "Fenotipado OGTT (metodología Stanford)", "Notas SOAP + exportación PDF", "Señales onco-metabólicas"],
      bullets_clinic: ["Panel de administración multipaciente", "Extensiones de algoritmos personalizadas", "Informes PDF white-label con tu logotipo", "Enterprise SDK y paquete Python"],
      cta_patient: "Iniciar evaluación gratuita", cta_doctor: "Ver funciones para Clínica", cta_clinic: "Contactar con el equipo",
    },
    science: {
      eyebrow: "Ciencia",
      title: "Creado con médicos. Validado por publicaciones.",
      sub: "Cada algoritmo está respaldado por investigación clínica revisada por pares. Fórmulas transparentes, código abierto, citas trazables.",
      placeholder: "Próximamente",
    },
    pricing: {
      eyebrow: "Precios",
      title: "Precios transparentes, sin sorpresas",
      sub: "Precios ajustados por PPP regional. Empieza gratis — sin tarjeta de crédito.",
      cta: "Ver planes",
    },
    citations: {
      eyebrow: "CIENCIA REVISADA POR PARES",
      title: "Construido sobre ciencia, no suposiciones",
      sub: "El modelo de insulinogenic budget de Nutramilo se basa en investigación clínica real sobre índices de insulina, síndrome metabólico y endocrinología nutricional.",
      cta: "Ver todas las publicaciones",
      library_cta: "Biblioteca científica",
    },
    sticky: { trial: "Prueba gratuita de 30 días", cta: "Ver planes →" },
    about: {
      heading: "¿Qué es Nutramilo?",
      body: "Nutramilo es una plataforma educativa de salud metabólica personalizada. A partir de sus análisis, medidas corporales y diario de comidas construye una red de 20 gemelos de los sistemas del organismo, unidos por más de 110 dependencias fisiológicas. Cada puntuación muestra de qué está compuesta y la plataforma exporta un informe detallado para comentar con su médico.",
      note: "No es un producto sanitario. No sustituye al médico.",
      privacy: "Cómo usamos sus datos",
    },
    twins_net: {
      eyebrow: "Arquitectura fisiológica",
      title_prefix: "gemelos para sistemas distintos. Un solo equilibrio común.",
      sub: "Cada gemelo responde de un sistema definido con precisión y recibe solo los datos que le corresponden. Juntos forman una red viva en la que ningún marcador se pierde ni se cuenta dos veces.",
      filter_all: "Todos los gemelos",
      filter_organs: "Metabolismo y órganos",
      filter_lifestyle: "Vida diaria",
      filter_clinical: "Clínicos y endocrinos",
    },
    circles: {
      eyebrow: "Biología de sistemas",
      title: "Más de 110 conexiones fisiológicas y detección de círculos viciosos",
      sub: "La tensión nerviosa no se queda en el sistema nervioso — frena la conversión de la hormona tiroidea. Una noche corta cambia la respuesta a la insulina al día siguiente. La red ordena estas dependencias y muestra qué círculo arde hoy y dónde está la palanca.",
    },
    capabilities: {
      eyebrow: "Cómo funciona en el día a día",
      title: "Lo que introduce en un sitio llega a todos los gemelos a los que afecta",
      sub: "Ninguna sección existe por sí sola. El resultado de un análisis, una comida o una anotación de «hoy» cambian las puntuaciones de los gemelos que dependen de ellos — y cada cambio lleva su motivo.",
      note_title: "Sin cálculos ocultos",
      note_body: "Cada puntuación se abre y declara de qué está compuesta: los marcadores que el gemelo puntúa, los que usa como contexto y las secciones que la han bajado — con el motivo de cada bajada. La plataforma ordena sus propios datos y tiene una finalidad educativa; no establece un diagnóstico ni sustituye una consulta médica.",
      cta_primary: "Cree una cuenta",
      cta_secondary: "Cómo funciona la red",
    },
    final: {
      title: "¿Listo para entender tu cuerpo?",
      sub: "Empieza con una evaluación gratuita de 60 segundos. Sin tarjeta de crédito.",
      cta: "Empezar",
      disclaimer: "Nutramilo no es un dispositivo médico según MDR 2017/745. No sustituye la consulta médica.",
    },
  },
};

// Avatar URLs from design blueprint
const AVATARS = {
  maria: "https://images.unsplash.com/photo-1484863137850-59afcfe05386?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  petar: "https://images.unsplash.com/photo-1757620765404-a1ee66df5e27?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  elena: "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
};

// ─────────────────────────────────────────────────────────────────────
// Smart Eating dropdown — surfaces 7 differentiating product modules.
// Each item links to a public info page (loaded lazily inside SubpageLayout).
// ─────────────────────────────────────────────────────────────────────
const SMART_EATING_ITEMS = [
  { key: "health-intelligence", icon: Activity,      to: "/smart/health-intelligence", bg: "Health Intelligence",   en: "Health Intelligence" },
  { key: "nutrition-lab",       icon: FlaskRound,    to: "/smart/nutrition-lab",       bg: "Nutrition Lab",         en: "Nutrition Lab" },
  { key: "plate",      icon: Utensils,      to: "/smart/plate-simulator",  bg: "Симулатор на чинията",  en: "Plate Simulator" },
  { key: "twin",       icon: Activity,      to: "/smart/digital-twin",     bg: "Digital Twin",          en: "Digital Twin" },
  { key: "planner",    icon: ClipboardList, to: "/smart/meal-planner",     bg: "AI Meal Planner",       en: "AI Meal Planner" },
  { key: "ask",        icon: Bot,           to: "/smart/ask-ai",           bg: "Попитай AI",            en: "Ask AI" },
  { key: "epigenetic", icon: Dna,           to: "/smart/epigenetic-map",   bg: "Епигенетична карта",    en: "Epigenetic Map" },
  { key: "clinical",   icon: Stethoscope,   to: "/smart/clinical-analysis", bg: "Клиничен анализ",      en: "Clinical Analysis" },
  { key: "lab",        icon: FlaskRound,    to: "/smart/digital-lab",      bg: "Дигитална лаборатория", en: "Digital Lab" },
];

// iter733 — Public standalone tools (moved off landing page for dedicated URLs).
const TOOLS_ITEMS = [
  { key: "scanner",  icon: ScanLine, to: "/tools/scanner",  bg: "AI Nutrition Scanner",  en: "AI Nutrition Scanner",
    subBg: "Снимка на етикет → DIL & NIS",              subEn: "Snap a label → DIL & NIS" },
  { key: "collagen", icon: Sparkles, to: "/tools/collagen", bg: "Колаген тип III · игра", en: "Collagen Type-3 · game",
    subBg: "Игра + сканиране на храни · COL3 кофактори", subEn: "Game + food scan · COL3 cofactors" },
];

function SmartEatingDropdown({ isDark, t }) {
  const { language } = useLanguage();
  const bg = language === "bg";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="landing-nav-smart"
          className={`inline-flex items-center gap-1 text-sm font-medium outline-none ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}
        >
          {t.nav.smart}
          <ChevronDown className="w-3.5 h-3.5 mt-0.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className={`min-w-[260px] p-1 ${isDark ? "bg-[hsl(222,47%,9%)] border-[hsl(223,47%,18%)]" : ""}`}
      >
        {SMART_EATING_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem key={item.key} asChild data-testid={`landing-nav-smart-${item.key}`}>
              <Link to={item.to} className="cursor-pointer flex items-center gap-2.5 px-2 py-2 rounded-md">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${isDark ? "bg-[hsl(283,49%,40%)]/20 text-[#e44288]" : "bg-[#5e227f]/10 text-[#5e227f]"}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-medium">{bg ? item.bg : item.en}</span>
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// iter733 — dropdown listing the two public, standalone tools.
function ToolsDropdown({ isDark, t }) {
  const { language } = useLanguage();
  const bg = language === "bg";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="landing-nav-tools"
          className={`inline-flex items-center gap-1 text-sm font-medium outline-none ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}
        >
          {t.nav.tools}
          <ChevronDown className="w-3.5 h-3.5 mt-0.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className={`min-w-[320px] p-1 ${isDark ? "bg-[hsl(222,47%,9%)] border-[hsl(223,47%,18%)]" : ""}`}
      >
        {TOOLS_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem key={item.key} asChild data-testid={`landing-nav-tools-${item.key}`}>
              <Link to={item.to} className="cursor-pointer flex items-start gap-2.5 px-2 py-2 rounded-md">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0 mt-0.5 ${isDark ? "bg-sky-500/20 text-sky-300" : "bg-sky-100 text-sky-700"}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="flex flex-col min-w-0">
                  <span className={`text-sm font-medium ${isDark ? "text-slate-100" : "text-slate-900"}`}>{bg ? item.bg : item.en}</span>
                  <span className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>{bg ? item.subBg : item.subEn}</span>
                </span>
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Navigation (top bar)
// ─────────────────────────────────────────────────────────────────────
export function LandingNav({ t, logo }) {
  const { language, setLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const bg = language === "bg";
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <nav
      data-testid="landing-nav"
      className={`sticky top-0 z-40 backdrop-blur-xl border-b ${isDark ? "bg-[hsl(223,47%,6%)]/90 border-[hsl(223,47%,12%)]" : "bg-white/90 border-slate-100"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="landing-nav-brand" className="flex items-center">
          {/* iter1078 — на телефон само иконата, за да се чете ясно */}
          <img src="/app-icon.png" alt="Nutramilo" className="h-9 w-9 object-contain sm:hidden" data-testid="landing-logo-mark" />
          <img src={logo || "/nutramilo-logo.png"} alt="Nutramilo" className="hidden sm:block h-9 w-auto object-contain" />
        </Link>
        <div className="hidden md:flex items-center gap-6">
          <a href="#how" data-testid="landing-nav-how" className={`text-sm font-medium ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{t.nav.how}</a>
          <SmartEatingDropdown isDark={isDark} t={t} />
          <ToolsDropdown isDark={isDark} t={t} />
          <Link to="/platform" data-testid="landing-nav-platform" className={`text-sm font-medium ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Платформата" : "Platform"}</Link>
          <Link to="/pricing" data-testid="landing-nav-pricing" className={`text-sm font-medium ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{t.nav.pricing}</Link>
          <Link to="/about" data-testid="landing-nav-about" className={`text-sm font-medium ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{t.nav.about}</Link>
          <Link to="/consultants" data-testid="landing-nav-consultants" className={`text-sm font-medium ${isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{t.nav.consultants}</Link>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="landing-lang-toggle" className={`p-2 rounded-lg text-xs font-bold ${isDark ? "hover:bg-white/5 text-slate-300" : "hover:bg-slate-100 text-slate-600"}`}>
                <Globe className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {SUPPORTED_LANGUAGES.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLanguage(l.code)} data-testid={`landing-lang-${l.code}`}>
                  <span className="text-xs font-bold w-7">{l.label}</span>
                  <span className="text-sm">{l.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={toggleTheme} data-testid="landing-theme-toggle" className={`p-2 rounded-lg ${isDark ? "hover:bg-white/5 text-slate-300" : "hover:bg-slate-100 text-slate-600"}`}>
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <Link to="/dashboard">
              <Button size="sm" data-testid="landing-nav-dashboard-btn" className="brand-cta-primary rounded-full px-5 text-xs font-semibold">{t.nav.dashboard}</Button>
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="landing-nav-signin" className={`hidden sm:inline-flex text-xs font-semibold px-3 py-1.5 rounded-full ${isDark ? "text-slate-300 hover:text-white hover:bg-white/5" : "text-slate-700 hover:text-[#287a2e] hover:bg-slate-100"}`}>
                {t.nav.signin}
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="landing-nav-signup-btn" className="brand-cta-primary rounded-full px-5 text-xs font-semibold">{t.nav.signup}</Button>
              </Link>
            </>
          )}
          {/* Mobile hamburger — visible only below md */}
          <button
            type="button"
            data-testid="landing-nav-mobile-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className={`md:hidden p-2 rounded-lg ${isDark ? "hover:bg-white/5 text-slate-300" : "hover:bg-slate-100 text-slate-700"}`}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="landing-nav-mobile-drawer"
          className={`md:hidden border-t ${isDark ? "border-[hsl(223,47%,12%)] bg-[hsl(223,47%,6%)]" : "border-slate-100 bg-white"}`}
        >
          <div className="px-4 py-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <a
              href="#how" onClick={() => setMobileOpen(false)}
              data-testid="landing-nav-mobile-how"
              className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${isDark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
            >{t.nav.how}</a>

            {/* Smart Eating accordion */}
            <details className="group">
              <summary
                data-testid="landing-nav-mobile-smart-summary"
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer list-none ${isDark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <span>{t.nav.smart}</span>
                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-1 ml-3 pl-3 border-l space-y-0.5" style={{ borderColor: isDark ? "hsl(223,47%,18%)" : "rgb(241,245,249)" }}>
                {SMART_EATING_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`landing-nav-mobile-smart-${item.key}`}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium ${isDark ? "text-slate-300 hover:bg-white/5" : "text-slate-600 hover:bg-slate-100"}`}
                    >
                      <Icon className="w-3.5 h-3.5 text-[#e44288] flex-shrink-0" />
                      {bg ? item.bg : item.en}
                    </Link>
                  );
                })}
              </div>
            </details>

            {/* iter733 — Tools accordion (mobile) */}
            <details className="group">
              <summary
                data-testid="landing-nav-mobile-tools-summary"
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer list-none ${isDark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <span>{t.nav.tools}</span>
                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-1 ml-3 pl-3 border-l space-y-0.5" style={{ borderColor: isDark ? "hsl(223,47%,18%)" : "rgb(241,245,249)" }}>
                {TOOLS_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`landing-nav-mobile-tools-${item.key}`}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium ${isDark ? "text-slate-300 hover:bg-white/5" : "text-slate-600 hover:bg-slate-100"}`}
                    >
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? "text-sky-300" : "text-sky-600"}`} />
                      {bg ? item.bg : item.en}
                    </Link>
                  );
                })}
              </div>
            </details>

            <Link
              to="/platform" onClick={() => setMobileOpen(false)}
              data-testid="landing-nav-mobile-platform"
              className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${isDark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
            >{bg ? "Платформата" : "Platform"}</Link>
            <Link
              to="/pricing" onClick={() => setMobileOpen(false)}
              data-testid="landing-nav-mobile-pricing"
              className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${isDark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
            >{t.nav.pricing}</Link>
            <Link
              to="/about" onClick={() => setMobileOpen(false)}
              data-testid="landing-nav-mobile-about"
              className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${isDark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
            >{t.nav.about}</Link>
            <Link
              to="/consultants" onClick={() => setMobileOpen(false)}
              data-testid="landing-nav-mobile-consultants"
              className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${isDark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
            >{t.nav.consultants}</Link>

            {!user && (
              <div className="pt-3 mt-2 border-t flex gap-2" style={{ borderColor: isDark ? "hsl(223,47%,12%)" : "rgb(241,245,249)" }}>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1">
                  <Button data-testid="landing-nav-mobile-signin" variant="outline" size="sm" className="w-full rounded-full text-xs font-semibold">{t.nav.signin}</Button>
                </Link>
                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1">
                  <Button data-testid="landing-nav-mobile-signup" size="sm" className="w-full brand-cta-primary rounded-full text-xs font-semibold">{t.nav.signup}</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 1 — Hero & Outcome Wall
// ─────────────────────────────────────────────────────────────────────

// iter882 — Compact "purpose" strip required by Google OAuth review
// ("Your home page does not explain the purpose of your app"). Deliberately
// minimal — one sentence + one paragraph + link out to /privacy for detail.
// Design principle: no duplication of hero copy, no marketing fluff.
function AboutNutramiloSection({ t, isDark }) {
  const tx = t.about;

  return (
    <section
      data-testid="about-nutramilo-section"
      className={`py-8 sm:py-10 ${isDark ? "bg-[hsl(223,47%,7%)]/40" : "bg-white/60"}`}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <h2
          className={`text-xl sm:text-2xl font-semibold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
          style={{ fontFamily: "'Work Sans', sans-serif" }}
        >
          {tx.heading}
        </h2>
        <p className={`text-sm sm:text-[15px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          {tx.body}
        </p>
        <p className={`mt-3 text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          {tx.note}
          {" · "}
          <a
            href="/privacy"
            className="underline hover:text-[#EC4899]"
            data-testid="about-privacy-link"
          >
            {tx.privacy}
          </a>
        </p>
      </div>
    </section>
  );
}

// Group 1 — Hero & Outcome Wall
// ─────────────────────────────────────────────────────────────────────
// iter1081 — Hero v2: продава един ясен резултат ("какво прави храненето с
// тялото Ви"), а архитектурата (20 близнака · 110 връзки) слиза под бутоните
// като доказателство и получава собствен блок по-долу.
function HeroPhonePreview({ t, isDark }) {
  const h = t.hero;
  return (
    <div className="flex justify-center lg:justify-end" data-testid="hero-phone-preview">
      <div className="w-full max-w-[330px]">
        <div
          className="rounded-[42px] p-[11px] bg-[#0B1220]"
          style={{ boxShadow: "0 40px 80px -30px rgba(15,23,42,.55), 0 0 0 1.5px #1E293B" }}
        >
          <div className={`rounded-[32px] overflow-hidden px-3 pt-3.5 pb-4 ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-[#F8FAFC]"}`}>
            <div className="flex justify-between px-2 pb-3 pt-0.5 text-[10px] text-slate-400">
              <span>9:41</span>
              <span>▮▮▮ ▂▄▆</span>
            </div>

            <div
              data-testid="hero-preview-card"
              className={`relative overflow-hidden rounded-[24px] p-4 border ${isDark ? "bg-[hsl(223,47%,13%)] border-[hsl(223,47%,20%)]" : "bg-white border-slate-100 shadow-sm"}`}
            >
              <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600" />
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-slate-400">
                {h.card_kicker}
              </div>
              <div className="mt-2.5 flex items-start gap-3">
                <span className="w-11 h-11 shrink-0 rounded-2xl grid place-items-center bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <Check className="w-5 h-5" strokeWidth={3} />
                </span>
                <div>
                  <div
                    className={`text-[16px] font-extrabold leading-snug ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
                    style={{ fontFamily: "'Work Sans', sans-serif" }}
                  >
                    {h.card_verdict}
                  </div>
                  <p className={`mt-1 text-[13px] leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {h.card_expl}
                  </p>
                  <p className="mt-1 text-[11px] text-slate-400">{h.card_nis}</p>
                </div>
              </div>
              <div className="mt-3.5 flex items-center justify-between gap-2.5">
                <span className="inline-flex items-center gap-1.5 min-h-[42px] px-4 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[12.5px] font-extrabold">
                  {h.card_cta} <ArrowRight className="w-3.5 h-3.5" />
                </span>
                <span className="text-[10.5px] text-slate-400 text-right leading-snug">
                  {h.card_score}<br />{h.card_score_label}
                </span>
              </div>
            </div>

            <div className={`mt-3 rounded-[20px] p-3 border opacity-50 ${isDark ? "bg-[hsl(223,47%,13%)] border-[hsl(223,47%,20%)]" : "bg-white border-slate-100"}`} aria-hidden>
              <div className="h-2.5 rounded-full bg-slate-200" />
              <div className="h-2.5 w-[55%] mt-2 rounded-full bg-slate-200" />
            </div>
          </div>
        </div>
        <p className="mt-3.5 text-center text-xs text-slate-400">{h.card_caption}</p>
      </div>
    </div>
  );
}

function HeroSteps({ t, isDark }) {
  const s = t.steps || COPY.bg.steps;
  return (
    <section
      id="how-it-works"
      data-testid="hero-steps-section"
      className={`border-t ${isDark ? "border-[hsl(223,47%,15%)] bg-[hsl(223,47%,7%)]" : "border-slate-200 bg-[#F8FAFC]"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-xs font-extrabold uppercase tracking-[0.08em] text-slate-400">{s.label}</div>
        <h2
          className={`mt-2.5 text-xl md:text-lg lg:text-2xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
          style={{ fontFamily: "'Work Sans', sans-serif" }}
        >
          {s.title}
        </h2>

        <div className="mt-6 grid gap-3.5 sm:grid-cols-3">
          {s.items.map((it, i) => (
            <div
              key={it.title}
              data-testid={`hero-step-${i + 1}`}
              className={`rounded-[20px] p-4 border transition-transform duration-300 hover:-translate-y-1 ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"}`}
            >
              <span className="w-8 h-8 rounded-xl grid place-items-center text-sm font-extrabold text-white brand-cta-primary" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                {i + 1}
              </span>
              <h3 className={`mt-3 text-base font-extrabold ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
                {it.title}
              </h3>
              <p className={`mt-1.5 text-sm leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>{it.desc}</p>
            </div>
          ))}
        </div>

        <div
          data-testid="hero-why-accurate"
          className={`mt-8 rounded-[24px] p-5 border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"}`}
        >
          <div className="text-xs font-extrabold uppercase tracking-[0.08em] text-slate-400">{s.why_label}</div>
          <h3 className={`mt-2 text-base md:text-lg font-extrabold ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {s.why_title}
          </h3>
          <ul className="mt-3 grid gap-2.5">
            {s.why.map((row) => (
              <li key={row} className={`flex gap-2.5 text-sm leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--brand-green-solid)" }} />
                <span>{row}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function HeroOutcomeWall({ t, isDark, avatars }) {
  const { language } = useLanguage();
  return (
    <>
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className={`absolute inset-0 ${isDark ? "" : "bg-[#F9F9F7]"}`} />
      <div className="brand-orb-purple" aria-hidden />
      <div className="brand-orb-green" aria-hidden />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-24">
        <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 lg:gap-14 items-center">
          <div>
            {/* Eyebrow */}
            <div data-testid="hero-eyebrow" className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-semibold ${isDark ? "border-[hsl(223,47%,22%)] text-slate-400 bg-[hsl(223,47%,12%)]" : "border-slate-200 text-slate-600 bg-white"}`}>
              <span className="w-[7px] h-[7px] rounded-full bg-emerald-500 ring-4 ring-emerald-500/20" />
              {t.hero.eyebrow}
            </div>
            {/* Headline */}
            <h1
              data-testid="hero-headline"
              className={`mt-5 font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
              style={{ fontFamily: "'Work Sans', sans-serif" }}
            >
              {t.hero.headline_1}<br />
              <span className="brand-gradient-text">{t.hero.headline_2}</span>
            </h1>
            {/* Subheadline */}
            <p data-testid="hero-sub" className={`mt-4 text-base sm:text-lg max-w-xl leading-relaxed ${isDark ? "text-slate-400" : "text-[#4A4A68]"}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {t.hero.sub}
            </p>
            {/* CTAs */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href="#landing-meal-ai" onClick={(e) => { e.preventDefault(); const el = document.getElementById("landing-meal-ai"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
                <Button data-testid="hero-cta-btn" className="brand-cta-primary w-full sm:w-auto px-7 min-h-[52px] text-sm font-bold rounded-full shadow-lg">
                  {t.hero.cta_primary} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#how-it-works">
                <Button data-testid="hero-secondary-btn" variant="outline" className={`w-full sm:w-auto px-6 min-h-[52px] text-sm rounded-full border ${isDark ? "border-slate-700 text-slate-300 hover:bg-white/5" : "border-slate-200 text-[#287a2e] hover:bg-[#287a2e]/5"}`}>
                  {t.hero.cta_secondary}
                </Button>
              </a>
            </div>
            {/* Trust ticks */}
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[13px]" data-testid="hero-trust-line">
              {[t.hero.trust_1, t.hero.trust_2, t.hero.trust_3].map((row) => (
                <span key={row} className={`inline-flex items-center gap-1.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  <Check className="w-3.5 h-3.5" style={{ color: "var(--brand-green-solid)" }} />
                  {row}
                </span>
              ))}
            </div>
            {/* Architecture as proof, not as promise */}
            <p data-testid="hero-proof-line" className="mt-3 text-xs leading-relaxed text-slate-400 max-w-xl">
              {t.hero.proof}
            </p>
            {/* MDR Compliance row */}
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="hero-compliance-row">
              <span data-testid="hero-md-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${isDark ? "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}>
                <Shield className="w-3 h-3" /> {t.hero.md_badge}
              </span>
            </div>
          </div>

          <HeroPhonePreview t={t} isDark={isDark} />
        </div>

        {/* Outcome wall — 3 micro-testimonials */}
        <div className="mt-12" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {t.testimonials.map((tm) => (
            <div
              key={tm.id}
              data-testid={`hero-testimonial-${tm.id}`}
              className={`relative rounded-xl p-5 border ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100 shadow-sm"}`}
            >
              <span
                data-testid={`hero-testimonial-badge-${tm.id}`}
                className="absolute -top-2 right-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white"
                style={{ background: "var(--brand-green-solid)" }}
              >
                {t.testimonial_badge || "30 дни"}
              </span>
              <div className="flex items-center gap-3 mb-3">
                <img src={(avatars || {})[`avatar_${tm.id}`] || AVATARS[tm.id]} alt={tm.name} className="w-10 h-10 rounded-full object-cover" />
                <div className="text-xs font-semibold" style={{ color: "var(--brand-green-solid)" }}>{tm.name}</div>
              </div>
              <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-[#1A1A2E]"}`}>
                &ldquo;{tm.text}&rdquo;
              </p>
            </div>
          ))}
        </div>

        {/* iter1114 — verified public reviews (hidden if none approved yet) */}
        <PublicReviewsStrip isDark={isDark} bg={language === "bg"} />

        {/* Audience Tabs */}
        <AudienceTabs t={t} isDark={isDark} />
      </div>
    </section>
    <HeroSteps t={t} isDark={isDark} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Audience Tabs (under hero)
// ─────────────────────────────────────────────────────────────────────
function AudienceTabs({ t, isDark }) {
  const { audience, setAudience } = useAudience();
  const tabs = [
    { id: "patient", label: t.audience_tabs.patient, Icon: User },
    { id: "doctor", label: t.audience_tabs.doctor, Icon: Stethoscope },
    { id: "clinic", label: t.audience_tabs.clinic, Icon: Building2 },
  ];
  return (
    <div data-testid="audience-switcher-tabs" className="text-center">
      <div className={`text-xs uppercase tracking-widest mb-3 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
        {t.audience_tabs.title}
      </div>
      <div className={`inline-flex flex-wrap justify-center gap-1.5 p-1.5 rounded-full border ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200 shadow-sm"}`}>
        {tabs.map(({ id, label, Icon }) => {
          const active = audience === id;
          return (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setAudience(id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                active
                  ? "brand-cta-primary text-white shadow"
                  : isDark
                    ? "text-slate-300 hover:bg-white/5"
                    : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 1.5 — Platform Overview (Bento grid — 6 feature cards)
// Bridges hero (outcome promise) and how-it-works (3-step process) by
// concretely showing what the user gets inside the app.
// ─────────────────────────────────────────────────────────────────────

// Per-feature visual config (icon + brand-tinted accent + target route)
const FEATURE_VISUALS = {
  "health-intelligence": { Icon: Activity, accent: "var(--brand-purple-deep)", route: "/dashboard/health-intelligence", span: "" },
  "nutrition-lab":       { Icon: FlaskRound, accent: "var(--brand-pink)",      route: "/dashboard/nutrition-lab",      span: "" },
  diary:    { Icon: BookOpen,     accent: "var(--brand-pink)",          route: "/assessment", span: "" },
  analyses: { Icon: Activity,     accent: "var(--brand-purple-mid)",    route: "/assessment", span: "" },
  ai:       { Icon: MessageCircle,accent: "var(--brand-purple-deep)",   route: "/assessment", span: "" },
  labs:     { Icon: FileSearch,   accent: "var(--brand-teal-deep)",     route: "/anonymous-lab", span: "" },
  twin:     { Icon: Microscope,   accent: "var(--brand-green-solid)",   route: "/assessment", span: "" },
  screen:   { Icon: ScanLine,     accent: "var(--brand-green-mid)",     route: "/dental-screening", span: "" },
};

function PlatformOverview({ t, isDark }) {
  const [openId, setOpenId] = React.useState(null);
  const p = t.platform;
  return (
    <section data-testid="platform-overview-section" className={`py-12 sm:py-16 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--brand-pink)" }}>{p.eyebrow}</div>
          <h2
            data-testid="platform-overview-title"
            className={`text-2xl sm:text-3xl font-semibold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
            style={{ fontFamily: "'Work Sans', sans-serif" }}
          >
            {p.title}
          </h2>
          <p className={`text-sm max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}>{p.sub}</p>
        </div>

        {/* iter592 — Три интелигентни слоя (Task 2): Health / Nutrition / Precision */}
        {(p.layers || []).map((layer, li) => {
          const feats = p.features.filter((x) => x.layer === layer.id);
          if (!feats.length) return null;
          return (
        <div key={layer.id} className={`max-w-5xl mx-auto ${li === 0 ? "" : "mt-12"}`} data-testid={`platform-layer-${layer.id}`}>
          <div className="flex items-start gap-3 mb-4">
            <span className="text-xs font-bold tabular-nums mt-1.5" style={{ color: "var(--brand-pink)" }}>0{li + 1}</span>
            <div>
              <h3 className={`text-lg sm:text-xl font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
                {layer.title}
              </h3>
              <p className={`text-xs sm:text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>{layer.tagline}</p>
            </div>
          </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {feats.map((f) => {
            const v = FEATURE_VISUALS[f.id] || FEATURE_VISUALS.diary;
            const Icon = v.Icon;
            const isOpen = openId === f.id;
            return (
              <div
                key={f.id}
                data-testid={`platform-feature-${f.id}`}
                className={`group relative rounded-2xl p-4 sm:p-5 border transition-all hover:-translate-y-0.5 flex flex-col ${
                  isDark
                    ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)] hover:border-[hsl(223,47%,28%)]"
                    : "bg-white border-slate-100 shadow-sm hover:shadow-md"
                } ${v.span}`}
              >
                {/* Media slot — compact brand-tinted icon block; will host screencast WebP/MP4 once recorded */}
                <div
                  data-testid={`platform-feature-${f.id}-media`}
                  className="relative w-full h-24 sm:h-28 rounded-lg mb-3 overflow-hidden flex items-center justify-center"
                  style={{
                    background: isDark
                      ? `linear-gradient(135deg, ${v.accent}22, transparent)`
                      : `linear-gradient(135deg, ${v.accent}1A, transparent 70%)`,
                  }}
                >
                  <Icon className="w-8 h-8 sm:w-9 sm:h-9" style={{ color: v.accent }} />
                </div>

                <h3
                  className={`text-sm sm:text-base font-semibold mb-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
                  style={{ fontFamily: "'Work Sans', sans-serif" }}
                >
                  {f.title}
                </h3>
                <p className={`text-xs sm:text-sm leading-snug mb-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{f.desc}</p>

                {/* Learn-more expander — shows our proprietary index explainers / educational context */}
                {f.learnMore && (
                  <>
                    <button
                      type="button"
                      data-testid={`platform-feature-${f.id}-learn`}
                      onClick={() => setOpenId(isOpen ? null : f.id)}
                      className="text-xs font-medium underline-offset-2 hover:underline self-start mb-2"
                      style={{ color: v.accent, background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                    >
                      {isOpen ? `↑ ${t.platform.learn}` : `↓ ${t.platform.learn}`}
                    </button>
                    {isOpen && (
                      <p
                        data-testid={`platform-feature-${f.id}-learn-text`}
                        className={`text-xs leading-relaxed mb-3 pl-3 border-l-2 ${isDark ? "text-slate-400 border-slate-700" : "text-slate-600 border-slate-200"}`}
                      >
                        {f.learnMore}
                      </p>
                    )}
                  </>
                )}

                {/* Primary destination */}
                <Link to={v.route} className="mt-auto inline-flex items-center gap-1 text-xs font-medium" style={{ color: v.accent }} data-testid={`platform-feature-${f.id}-link`}>
                  {f.linkBg} <ArrowRight className="w-3 h-3" />
                </Link>

                {/* Optional sub-links (used for screenings: skin/teeth/breast) */}
                {f.subLinks && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {f.subLinks.map((sl) => (
                      <Link
                        key={sl.to}
                        to={sl.to}
                        data-testid={`platform-feature-${f.id}-sublink-${sl.label.toLowerCase()}`}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                          isDark
                            ? "border-[hsl(223,47%,22%)] text-slate-400 hover:text-white hover:border-[hsl(223,47%,32%)]"
                            : "border-slate-200 text-slate-600 hover:text-[#287a2e] hover:border-[#287a2e]"
                        }`}
                      >
                        {sl.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// iter592 — Task 3: Digital Twin като ядро на платформата (визуална схема)
// ─────────────────────────────────────────────────────────────────────
function DigitalTwinCore({ t, isDark }) {
  const tc = t.twin_core;
  if (!tc) return null;
  const nodeBase = `rounded-2xl border px-4 py-3 text-center ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100 shadow-sm"}`;
  return (
    <section data-testid="digital-twin-core-section" className={`py-16 sm:py-20 ${isDark ? "" : "bg-white"}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--brand-pink)" }}>{tc.eyebrow}</div>
          <h2 data-testid="twin-core-title" className={`text-3xl sm:text-4xl font-semibold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {tc.title}
          </h2>
          <p className={`text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}>{tc.sub}</p>
        </div>

        {/* Поток: входни данни → AI → близнак → препоръки */}
        <div className="flex flex-col items-center gap-3" data-testid="twin-core-flow">
          <div className="flex flex-wrap justify-center gap-2">
            {tc.inputs.map((inp) => (
              <span key={inp} className={`text-xs sm:text-sm font-medium px-3.5 py-1.5 rounded-full border ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,20%)] text-slate-300" : "bg-[#F9F9F7] border-slate-200 text-slate-700"}`}>
                {inp}
              </span>
            ))}
          </div>
          <span className={isDark ? "text-slate-600" : "text-slate-300"} aria-hidden="true">↓</span>
          <div className={`${nodeBase} text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>{tc.ai_label}</div>
          <span className={isDark ? "text-slate-600" : "text-slate-300"} aria-hidden="true">↓</span>
          <div
            data-testid="twin-core-node"
            className="rounded-2xl px-8 py-5 text-center text-white shadow-lg"
            style={{ background: "var(--brand-gradient-primary)" }}
          >
            <div className="text-lg sm:text-xl font-bold" style={{ fontFamily: "'Work Sans', sans-serif" }}>{tc.twin_label}</div>
          </div>
          <span className={isDark ? "text-slate-600" : "text-slate-300"} aria-hidden="true">↓</span>
          <div className={`${nodeBase} text-sm font-semibold`} style={{ color: "var(--brand-green-solid)" }}>{tc.out_label}</div>
        </div>

        <p className={`text-center text-xs mt-8 max-w-xl mx-auto ${isDark ? "text-slate-500" : "text-slate-500"}`}>{tc.note}</p>
        <div className="text-center mt-5">
          <Link to="/smart/digital-twin" data-testid="twin-core-cta" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--brand-purple-deep)" }}>
            {tc.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// iter592 — Task 7: Продуктова история (Събираме данни → ... → Резултати)
// ─────────────────────────────────────────────────────────────────────
function ProductStory({ t, isDark }) {
  const s = t.story;
  if (!s) return null;
  return (
    <section data-testid="product-story-section" className={`py-16 sm:py-20 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--brand-pink)" }}>{s.eyebrow}</div>
          <h2 data-testid="product-story-title" className={`text-3xl sm:text-4xl font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {s.title}
          </h2>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {s.steps.map((st, i) => (
            <li
              key={i}
              data-testid={`product-story-step-${i}`}
              className={`relative rounded-2xl p-4 sm:p-5 border ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100 shadow-sm"}`}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white mb-3" style={{ background: "var(--brand-gradient-primary)" }}>
                {i + 1}
              </div>
              <h3 className={`text-sm font-semibold mb-1.5 leading-snug ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{st.title}</h3>
              <p className={`text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>{st.desc}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 2 — How it Works (3 steps + Lottie placeholder)
// ─────────────────────────────────────────────────────────────────────
function HowItWorks({ t, isDark }) {
  return (
    <section id="how" data-testid="how-it-works-section" className={`py-20 sm:py-28 ${isDark ? "" : "bg-white"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--brand-pink)" }}>{t.how.eyebrow}</div>
          <h2 className={`text-3xl sm:text-4xl font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {t.how.title}
          </h2>
        </div>
        {/* Lottie placeholder card */}
        <LottieHeroPlaceholder t={t} isDark={isDark} />
        {/* 3 steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {t.how.steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`how-step-${i}`}
                className={`rounded-2xl p-7 border relative ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100 shadow-sm"}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background: i === 0
                      ? "var(--brand-gradient-primary)"
                      : i === 1
                        ? "var(--brand-gradient-nature)"
                        : "var(--brand-green-solid)",
                    color: "#fff",
                  }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div className={`text-xs font-bold mb-1 ${isDark ? "text-slate-500" : "text-slate-400"}`}>0{i + 1}</div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{s.title}</h3>
                <p className={`text-sm leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Lottie hero placeholder — visually-rich stand-in until the real Lottie is delivered
function LottieHeroPlaceholder({ t, isDark }) {
  return (
    <div
      data-testid="hero-lottie-placeholder"
      className={`relative rounded-3xl overflow-hidden aspect-[16/8] sm:aspect-[16/6] border ${isDark ? "border-[hsl(223,47%,18%)]" : "border-slate-100 shadow-xl"}`}
      style={{
        background: isDark
          ? "linear-gradient(135deg, hsl(223,47%,10%), hsl(223,47%,6%))"
          : "linear-gradient(135deg, #f8f7fc, #ffffff)",
      }}
    >
      <div className="absolute inset-0 brand-orb-purple" aria-hidden />
      <div className="absolute inset-0 brand-orb-green" aria-hidden />
      <div className="relative h-full flex items-center justify-center p-6">
        <div className="text-center max-w-xl">
          {/* Mock VAIS dial */}
          <div className="inline-flex flex-col items-center gap-2 mb-4">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white"
              style={{ background: "var(--brand-gradient-primary)" }}
            >
              52
            </div>
            <div className={`text-xs uppercase tracking-widest ${isDark ? "text-slate-400" : "text-slate-500"}`}>VAIS Score</div>
          </div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {t.how.lottie_caption}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 3 — See it in action (audience-personalised)
// ─────────────────────────────────────────────────────────────────────
function SeeItInAction({ t, isDark }) {
  const { audience } = useAudience();
  const title = t.action[`title_${audience}`];
  const sub = t.action[`sub_${audience}`];
  const bullets = t.action[`bullets_${audience}`];
  const cta = t.action[`cta_${audience}`];
  const ctaHref = audience === "patient" ? "/assessment" : audience === "doctor" ? "/pricing#clinic" : "/about";
  return (
    <section data-testid="see-in-action-section" className={`py-20 sm:py-28 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--brand-pink)" }}>{t.action.eyebrow}</div>
            <h2 data-testid={`see-action-title-${audience}`} className={`text-3xl sm:text-4xl font-semibold mb-4 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>{title}</h2>
            <p className={`text-base leading-relaxed mb-6 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{sub}</p>
            <ul className="space-y-2.5 mb-7">
              {bullets.map((b, i) => (
                <li key={i} data-testid={`see-action-bullet-${audience}-${i}`} className={`flex items-start gap-2.5 text-sm ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--brand-green-solid)" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link to={ctaHref}>
              <Button data-testid={`see-action-cta-${audience}`} className="brand-cta-primary px-6 h-11 text-sm font-semibold rounded-full">
                {cta} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
          <ActionVisual audience={audience} isDark={isDark} />
        </div>
      </div>
    </section>
  );
}

function ActionVisual({ audience, isDark }) {
  // Audience-specific visual stat card (no external image dependencies for reliability)
  const stats = {
    patient: [
      { k: "VAIS", v: "52", d: "↓ от 78" },
      { k: "NIS среден", v: "44", d: "↓ -23%" },
      { k: "Стрийк", v: "12 дни", d: "хранителен дневник" },
    ],
    doctor: [
      { k: "Време/пациент", v: "−22 мин", d: "след автоматизация" },
      { k: "OCR точност", v: "97%", d: "за стандартни PDF" },
      { k: "OGTT phenotypes", v: "4", d: "Stanford методология" },
    ],
    clinic: [
      { k: "Пациентски слотове", v: "100", d: "включени в Clinic" },
      { k: "Audit log", v: "100%", d: "GDPR-съответен" },
      { k: "SDK", v: "Python", d: "v0.5+ enterprise" },
    ],
  }[audience];
  return (
    <div
      data-testid={`action-visual-${audience}`}
      className={`rounded-3xl p-7 border relative overflow-hidden ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100 shadow-xl"}`}
      style={{ minHeight: "320px" }}
    >
      <div className="brand-orb-purple" aria-hidden />
      <div className="relative space-y-4">
        {stats.map((s, i) => (
          <div key={i} className={`flex items-baseline justify-between border-b pb-3 ${isDark ? "border-[hsl(223,47%,18%)]" : "border-slate-100"}`}>
            <div>
              <div className={`text-xs uppercase tracking-widest ${isDark ? "text-slate-500" : "text-slate-400"}`}>{s.k}</div>
              <div className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{s.d}</div>
            </div>
            <div className="text-2xl font-bold brand-gradient-text" style={{ fontFamily: "'Work Sans', sans-serif" }}>{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 4 — Science Trust Wall (Featured Doctors)
// ─────────────────────────────────────────────────────────────────────
function ScienceTrustWall({ t, isDark, language }) {
  const [experts, setExperts] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE}/research/experts`).then((r) => setExperts(r.data?.items || [])).catch(() => setExperts([]));
  }, []);
  // iter391 — show ONLY real experts (no "Joining soon" placeholder slots per founder request)
  return (
    <section data-testid="featured-doctors-section" className={`py-20 sm:py-28 ${isDark ? "" : "bg-white"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center mb-12">
        <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--brand-pink)" }}>{t.science.eyebrow}</div>
        <h2 className={`text-3xl sm:text-4xl font-semibold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>{t.science.title}</h2>
        <p className={`text-base max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}>{t.science.sub}</p>
      </div>
      {experts.length > 0 && (
        <div
          className={`max-w-6xl mx-auto px-4 sm:px-6 grid gap-4 sm:gap-5 ${
            experts.length === 1
              ? "grid-cols-1 max-w-sm"
              : experts.length <= 3
                ? "grid-cols-2 md:grid-cols-3"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
          }`}
        >
          {experts.map((e) => {
            const name = language === "bg" ? e.name_bg : e.name_en;
            const role = language === "bg" ? e.title_bg : e.title_en;
            const initials = name.split(" ").slice(-2).map((s) => s[0]).join("");
            return (
              <div key={e.slug} data-testid={`expert-card-${e.slug}`} className={`rounded-2xl p-5 border text-center ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100 shadow-sm"}`}>
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-semibold text-xl"
                  style={{ background: "var(--brand-gradient-primary)" }}
                >
                  {initials}
                </div>
                <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{name}</div>
                <div className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{role}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 4.5 — Data Sources Trust Wall (rebuilt from legacy InfoPage)
// ─────────────────────────────────────────────────────────────────────
function DataSourcesSection({ t, isDark }) {
  const s = t.sources;
  if (!s) return null;
  return (
    <section data-testid="data-sources-section" className={`py-20 sm:py-28 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--brand-pink)" }}>{s.eyebrow}</div>
          <h2 className={`text-3xl sm:text-4xl font-semibold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {s.title}
          </h2>
          <p className={`text-base max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}>{s.sub}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.items.map((item, i) => (
            <div
              key={item.name}
              data-testid={`data-source-${i}`}
              className={`rounded-xl p-5 border ${isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100 shadow-sm"}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--brand-gradient-primary)" }}
                >
                  <Check className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className={`text-sm font-semibold mb-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{item.name}</div>
                  <div className={`text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>{item.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 4.6 — Peer-Reviewed Science Citations (FEATURED_CITATIONS)
// ─────────────────────────────────────────────────────────────────────
const FEATURED_CITATIONS = [
  { author: "Tabung F.", title: "Development and validation of empirical indices to assess the insulinaemic potential of diet and lifestyle" },
  { author: "Bell K.", title: "Algorithms to Improve the Prediction of Postprandial Insulinaemia in Response to Common Foods" },
  { author: "Nimptsch K.", title: "Dietary insulin index and insulin load in relation to biomarkers of glycemic control, plasma lipids, and inflammation markers" },
  { author: "Mirmiran P.", title: "Dietary insulin load and insulin index are associated with the risk of insulin resistance — Tehran Lipid and Glucose Study" },
  { author: "Sadeghi O.", title: "Dietary Insulin Index and Dietary Insulin Load in Relation to Metabolic Syndrome: The Shahedieh Cohort Study" },
  { author: "Bao Y.", title: "Dietary Insulin Load, Dietary Insulin Index, and Colorectal Cancer" },
  { author: "Lee D.", title: "Dietary Inflammatory and Insulinemic Potential and Risk of Type 2 Diabetes: Three Prospective U.S. Cohort Studies" },
  { author: "Vajdi M.", title: "Dietary insulin index and load and cardiometabolic risk factors among people with obesity" },
];

function PeerReviewedScienceSection({ t, isDark }) {
  const c = t.citations;
  if (!c) return null;
  return (
    <section data-testid="peer-reviewed-section" className={`py-20 sm:py-24 ${isDark ? "bg-[hsl(223,47%,6%)]" : "bg-gradient-to-b from-white to-[#F9F9F7]"}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--brand-pink)" }}>
            {c.eyebrow}
          </p>
          <h2 className={`text-2xl sm:text-3xl font-semibold tracking-tight mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {c.title}
          </h2>
          <p className={`text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-[#4A4A68]"}`}>
            {c.sub}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {FEATURED_CITATIONS.map((cit, i) => (
            <div
              key={cit.title}
              data-testid={`peer-citation-${i}`}
              className={`flex items-start gap-3 p-4 rounded-xl transition-colors ${
                isDark ? "bg-[hsl(223,47%,10%)] hover:bg-[hsl(223,47%,13%)]" : "bg-white hover:bg-slate-50 border border-slate-100"
              }`}
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: "var(--brand-gradient-primary)" }}
              >
                <BookOpen className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-semibold mb-0.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{cit.author}</p>
                <p className={`text-xs leading-snug ${isDark ? "text-slate-400" : "text-[#4A4A68]"}`}>{cit.title}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/knowledge-library">
            <Button
              data-testid="peer-reviewed-library-btn"
              className="brand-cta-primary rounded-full px-7 h-11 text-sm font-semibold"
            >
              {c.library_cta} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Group 5 — Pricing teaser + Final CTA
// ─────────────────────────────────────────────────────────────────────
function PricingFinalCTA({ t, isDark }) {
  return (
    <section data-testid="final-cta-section" className={`py-20 sm:py-28 relative overflow-hidden ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-[#F9F9F7]"}`}>
      <div className="brand-orb-purple" aria-hidden />
      <div className="brand-orb-green" aria-hidden />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 className={`text-3xl sm:text-5xl font-semibold mb-8 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
          {t.final.title}
        </h2>
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <Link to="/pricing">
            <Button data-testid="final-cta-pricing-btn" className="brand-cta-primary px-7 h-12 text-sm font-semibold rounded-full shadow-lg">
              {t.pricing.cta} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/assessment">
            <Button data-testid="final-cta-assessment-btn" variant="outline" className={`px-6 h-12 text-sm rounded-full ${isDark ? "border-slate-700 text-slate-300 hover:bg-white/5" : "border-slate-200 text-[#287a2e] hover:bg-[#287a2e]/5"}`}>
              {t.final.cta}
            </Button>
          </Link>
        </div>
        <p data-testid="final-cta-disclaimer" className={`text-xs max-w-xl mx-auto ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          {t.final.disclaimer}
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sticky Bottom CTA — appears post-hero
// ─────────────────────────────────────────────────────────────────────
function StickyBottomCTA({ t, isDark }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 700);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <div
      data-testid="sticky-bottom-cta"
      className={`fixed bottom-0 left-0 right-0 z-30 border-t backdrop-blur-xl ${isDark ? "bg-[hsl(223,47%,6%)]/95 border-[hsl(223,47%,12%)]" : "bg-white/95 border-slate-100"}`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div className={`text-sm font-medium hidden sm:block ${isDark ? "text-slate-300" : "text-slate-700"}`}>
          ✓ {t.sticky.trial}
        </div>
        <Link to="/pricing">
          <Button data-testid="sticky-cta-btn" className="brand-cta-primary rounded-full px-6 h-10 text-sm font-semibold">
            {t.sticky.cta}
          </Button>
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Minimal Footer
// ─────────────────────────────────────────────────────────────────────
function LandingFooter({ isDark, language, logo }) {
  const bg = language === "bg";
  return (
    <footer data-testid="landing-footer" className={`border-t py-12 ${isDark ? "border-[hsl(223,47%,12%)] bg-[hsl(223,47%,5%)]" : "border-slate-100 bg-white"}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
        <div className="col-span-2">
          <img src={logo || "/nutramilo-logo.png"} alt="Nutramilo" className="h-9 mb-3" />
          <p className={`text-xs leading-relaxed max-w-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {bg
              ? "Nutramilo е AI платформа за персонализирано здраве и превенция."
              : "Nutramilo is an AI platform for personalised health and prevention."}
          </p>
          <p className={`text-[11px] leading-relaxed max-w-sm mt-1.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {bg
              ? "Не поставя диагнози и не замества медицинска консултация."
              : "It does not diagnose and does not replace medical consultation."}
          </p>
          <p className={`text-xs mt-3 ${isDark ? "text-slate-600" : "text-slate-400"}`}>
            © {new Date().getFullYear()} International Sci Ink Press Ltd EOOD · ЕИК 205414288
          </p>
        </div>
        <div>
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{bg ? "Продукт" : "Product"}</div>
          <ul className="space-y-1.5">
            <li><Link data-testid="footer-link-pricing" to="/pricing" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Цени" : "Pricing"}</Link></li>
            <li><Link data-testid="footer-link-assessment" to="/assessment" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Безплатна оценка" : "Free assessment"}</Link></li>
            <li><Link data-testid="footer-link-technical" to="/technical" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Техническа спецификация" : "Technical spec"}</Link></li>
            <li><Link data-testid="footer-link-formulas" to="/scientific-formulas" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Научни формули" : "Scientific formulas"}</Link></li>
          </ul>
        </div>
        <div>
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{bg ? "Ресурси" : "Resources"}</div>
          <ul className="space-y-1.5">
            <li><Link data-testid="footer-link-platform" to="/platform" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Описание на платформата" : "Platform description"}</Link></li>
            <li><Link data-testid="footer-link-about" to="/about" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "За нас" : "About us"}</Link></li>
            <li><Link data-testid="footer-link-consultants" to="/consultants" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Консултанти" : "Consultants"}</Link></li>
            <li><Link data-testid="footer-link-guide" to="/guide" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Ръководство" : "Guide"}</Link></li>
            <li><Link data-testid="footer-link-faq" to="/faq" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Често задавани въпроси" : "FAQ"}</Link></li>
          </ul>
        </div>
        <div>
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{bg ? "Правни" : "Legal"}</div>
          <ul className="space-y-1.5">
            <li><Link data-testid="footer-link-legal" to="/legal" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "GDPR & Политики" : "GDPR & Policies"}</Link></li>
            <li><Link data-testid="footer-link-contact" to="/contact" className={`text-xs ${isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-[#287a2e]"}`}>{bg ? "Контакти" : "Contact"}</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────
// iter733 — ToolsTeaser (compact 2-card CTA on landing that routes to
// the dedicated tool pages)
// ─────────────────────────────────────────────────────────────────────
function ToolsTeaser({ isDark, bg }) {
  return (
    <section
      data-testid="landing-tools-teaser"
      className={`py-14 px-4 sm:px-6 lg:px-8 ${
        isDark ? "bg-gradient-to-b from-slate-950 via-sky-950/20 to-slate-950" : "bg-gradient-to-b from-white via-sky-50/30 to-white"
      }`}
    >
      <div className="max-w-5xl mx-auto text-center mb-8">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
          isDark ? "bg-sky-500/15 text-sky-300 border border-sky-500/30" : "bg-sky-100 text-sky-700 border border-sky-300"
        }`}>
          <Sparkles className="w-3 h-3" />
          {bg ? "Безплатни инструменти" : "Free tools"}
        </div>
        <h2 className={`text-3xl sm:text-4xl font-black mt-3 tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
          {bg ? "Изпробвай " : "Try our "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-purple-400 to-pink-400">
            {bg ? "два клинични инструмента" : "two clinical tools"}
          </span>
        </h2>
        <p className={`mt-2 text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          {bg
            ? "Без регистрация · без карта · директно в браузъра ти"
            : "No sign-up · no credit card · right in your browser"}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
        <Link
          to="/tools/scanner"
          data-testid="landing-teaser-scanner"
          className={`group block p-6 rounded-2xl border transition-all hover:scale-[1.01] hover:shadow-xl ${
            isDark ? "bg-gradient-to-br from-slate-900 to-sky-950/40 border-sky-500/30 hover:border-sky-400" : "bg-gradient-to-br from-white to-sky-50 border-slate-200 hover:border-sky-400 shadow-md"
          }`}
        >
          <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${isDark ? "bg-sky-500/20 text-sky-300" : "bg-sky-100 text-sky-700"}`}>
            <ScanLine className="w-5 h-5" />
          </div>
          <h3 className={`mt-4 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "AI Nutrition Facts Scanner" : "AI Nutrition Facts Scanner"}
          </h3>
          <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg
              ? "Снимай хранителен етикет → DIL & NIS моментално. Комбинирай до 20 продукта на ден за пълен клиничен ефект."
              : "Snap a nutrition label → instant DIL & NIS. Combine up to 20 products/day for the full clinical impact."}
          </p>
          <div className={`mt-4 inline-flex items-center gap-1 text-sm font-bold ${isDark ? "text-sky-300 group-hover:text-sky-200" : "text-sky-700 group-hover:text-sky-800"}`}>
            {bg ? "Отвори инструмент" : "Open tool"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>

        <Link
          to="/tools/collagen"
          data-testid="landing-teaser-collagen"
          className={`group block p-6 rounded-2xl border transition-all hover:scale-[1.01] hover:shadow-xl ${
            isDark ? "bg-gradient-to-br from-slate-900 to-purple-950/40 border-purple-500/30 hover:border-purple-400" : "bg-gradient-to-br from-white to-purple-50 border-slate-200 hover:border-purple-400 shadow-md"
          }`}
        >
          <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${isDark ? "bg-purple-500/20 text-purple-300" : "bg-purple-100 text-purple-700"}`}>
            <Sparkles className="w-5 h-5" />
          </div>
          <h3 className={`mt-4 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "Колаген тип III · игра" : "Collagen Type-3 · game"}
          </h3>
          <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg
              ? "Интерактивна образователна игра за COL3 плътността — с реален food scanner за 244+ храни."
              : "Interactive COL3 density education game — with a real-time food scanner over 244+ foods."}
          </p>
          <div className={`mt-4 inline-flex items-center gap-1 text-sm font-bold ${isDark ? "text-purple-300 group-hover:text-purple-200" : "text-purple-700 group-hover:text-purple-800"}`}>
            {bg ? "Играй сега" : "Play now"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────
// iter1005/iter1006/iter1007 — Hero video section (product demo)
// Viewport-aware: video is preloaded and autoplayed only when the section
// enters the visible viewport. Poster uses <picture> so AVIF (~9 KB) /
// WebP (~23 KB) are served before falling back to the JPEG (65 KB).
// ─────────────────────────────────────────────────────────────────────
function HeroVideoSection({ isDark, language, poster, videoSrc }) {
  const bg = language === "bg";
  const videoRef = React.useRef(null);
  const sectionRef = React.useRef(null);
  const [muted, setMuted] = React.useState(true);
  const [posterVisible, setPosterVisible] = React.useState(true);
  const [inView, setInView] = React.useState(false);

  // iter1007 — start fetching + playing ONLY once the section is visible.
  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      // Very old browsers: fall back to eager load.
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            const v = videoRef.current;
            if (v) {
              v.preload = "auto";
              const p = v.play();
              if (p && typeof p.catch === "function") p.catch(() => {});
            }
          } else {
            // Off-screen → pause to save CPU/battery.
            const v = videoRef.current;
            if (v && !v.paused) v.pause();
          }
        });
      },
      { threshold: 0.25, rootMargin: "0px 0px -10% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleSoundToggle = () => {
    const v = videoRef.current;
    if (!v) return;
    const nextMuted = !v.muted;
    v.muted = nextMuted;
    if (!nextMuted) {
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
    setMuted(nextMuted);
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-video-section"
      className={`py-12 sm:py-16 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-white"}`}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-6 sm:mb-8">
          <div
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide mb-3 ${
              isDark
                ? "bg-fuchsia-500/15 text-fuchsia-300 ring-1 ring-fuchsia-500/30"
                : "bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-200"
            }`}
            data-testid="hero-video-eyebrow"
          >
            <Sparkles className="w-3 h-3" />
            {bg ? "Виж Nutramilo в действие" : "See Nutramilo in action"}
          </div>
          <h2
            className={`text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter ${
              isDark ? "text-white" : "text-[#1A1A2E]"
            }`}
            style={{ fontFamily: "'Work Sans', sans-serif" }}
          >
            {bg ? "Твоето здраве. Свързано." : "Your health. Connected."}
          </h2>
        </div>

        <div
          className={`relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl ring-1 aspect-video ${
            isDark ? "ring-white/10 bg-black" : "ring-slate-200 bg-black"
          }`}
        >
          {/* iter1007 — <picture> lets the browser pick AVIF → WebP → JPEG.
              Sits above the <video> until the visitor triggers play; then
              fades out so the actual video shows through. */}
          {posterVisible && (
            <picture
              className="absolute inset-0 w-full h-full block pointer-events-none"
              data-testid="hero-video-poster"
            >
              {!poster && <source srcSet="/videos/hero-poster.avif" type="image/avif" />}
              {!poster && <source srcSet="/videos/hero-poster.webp" type="image/webp" />}
              <img
                src={poster || "/videos/hero-poster.jpg"}
                alt={bg
                  ? "Nutramilo — какво ако можеше да виждаш здравето си като система?"
                  : "Nutramilo — what if you could see your health as a system?"}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </picture>
          )}
          <video
            ref={videoRef}
            data-testid="hero-video-player"
            src={inView
              ? (videoSrc
                || "https://customer-assets-4nw71qhi.emergentagent.net/job_endocrine-hub-2/artifacts/tpakzxre_Nutramilo%20%E2%80%94%20Your%20Health.%20Connected.%20v1.mp4")
              : undefined}
            poster={poster || "/videos/hero-poster.jpg"}
            className="w-full h-full block absolute inset-0 object-cover"
            muted
            loop
            playsInline
            controls
            preload={inView ? "metadata" : "none"}
            onPlaying={() => setPosterVisible(false)}
            aria-label={bg
              ? "Продуктово демо на Nutramilo"
              : "Nutramilo product demo"}
          >
            {bg
              ? "Твоят браузър не поддържа видео."
              : "Your browser does not support the video tag."}
          </video>
        </div>

        {/* iter1006 — one-click "Play with sound" pill so visitors don't hunt
            for the mute control in the native player. */}
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={handleSoundToggle}
            data-testid="hero-video-sound-toggle"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition ring-1 shadow-sm ${
              muted
                ? isDark
                  ? "bg-fuchsia-500/15 text-fuchsia-200 ring-fuchsia-500/40 hover:bg-fuchsia-500/25"
                  : "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 hover:bg-fuchsia-100"
                : isDark
                  ? "bg-emerald-500/15 text-emerald-200 ring-emerald-500/40 hover:bg-emerald-500/25"
                  : "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100"
            }`}
          >
            {muted ? (
              <>
                <VolumeX className="w-4 h-4" />
                {bg ? "Пусни със звук" : "Play with sound"}
              </>
            ) : (
              <>
                <Volume2 className="w-4 h-4" />
                {bg ? "Изключи звука" : "Mute video"}
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Root component
// ─────────────────────────────────────────────────────────────────────
function LandingPageInner() {
  const { language } = useLanguage();
  const { isDark } = useTheme();

  // iter1069 — подредбата, скритите секции, текстовете и снимките идват от
  // управлението в админ панела. Празен отговор = текстовете от кода.
  const [cms, setCms] = useState({ order: [], hidden: [], texts: {}, images: {} });
  const preview = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("cms") === "draft";

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = preview
      ? `${base}/api/admin/landing/content?state=draft`
      : `${base}/api/landing/content`;
    axios.get(url)
      .then(({ data }) => setCms({
        order: data?.order || [], hidden: data?.hidden || [],
        texts: data?.texts || {}, images: data?.images || {},
      }))
      .catch(() => {});
  }, [preview]);

  const t = useMemo(
    () => applyTextOverrides(COPY[language] || COPY.bg, cms.texts?.[language]),
    [language, cms.texts],
  );
  const order = useMemo(() => resolveOrder(cms.order), [cms.order]);
  const hidden = useMemo(() => new Set(cms.hidden || []), [cms.hidden]);
  const img = (slot, fallback) => cms.images?.[slot] || fallback;
  const bgLang = language === "bg";

  const SECTIONS = {
    nav: <LandingNav t={t} logo={img("logo", "/nutramilo-logo.png")} />,
    hero: <HeroOutcomeWall t={t} isDark={isDark} avatars={cms.images} />,
    hero_video: <HeroVideoSection isDark={isDark} language={language}
      poster={img("hero_poster", null)} videoSrc={img("hero_video_src", null)} />,
    about: <AboutNutramiloSection t={t} isDark={isDark} />,
    tool_meal_ai: <LandingMealAI />,
    tool_quiz: <LandingNutritionQuiz />,
    tool_insulin_gallery: <LandingInsulinFoodGallery />,
    tool_dil: <LandingDilCalculator />,
    tool_personal_load: <LandingPersonalInsulinLoad />,
    tools_teaser: <ToolsTeaser isDark={isDark} bg={bgLang} />,
    twin_core: <DigitalTwinCore t={t} isDark={isDark} />,
    twins_net: <TwinNetworkSection isDark={isDark} lang={language}
      copy={t.twins_net || COPY.bg.twins_net} />,
    circles: <ViciousCirclesSection isDark={isDark} lang={language}
      copy={t.circles || COPY.bg.circles} />,
    capabilities: <PlatformCapabilitiesSection isDark={isDark} lang={language}
      copy={t.capabilities || COPY.bg.capabilities} />,
    founder: <FounderBand isDark={isDark} language={language} />,
    live_demo: <LiveTwinDemo isDark={isDark} bg={bgLang} />,
    platform: <PlatformOverview t={t} isDark={isDark} />,
    story: <ProductStory t={t} isDark={isDark} />,
    action: <SeeItInAction t={t} isDark={isDark} />,
    sources: <DataSourcesSection t={t} isDark={isDark} />,
    science: <PeerReviewedScienceSection t={t} isDark={isDark} />,
    pricing: <PricingFinalCTA t={t} isDark={isDark} />,
    footer: <LandingFooter isDark={isDark} language={language}
      logo={img("logo", "/nutramilo-logo.png")} />,
    sticky: <StickyBottomCTA t={t} isDark={isDark} />,
  };
  const LAZY = new Set(["tool_meal_ai", "tool_quiz", "tool_insulin_gallery",
    "tool_dil", "tool_personal_load", "tools_teaser"]);

  return (
    <div className={`min-h-screen ${isDark ? "bg-background text-foreground" : "bg-[#F9F9F7]"} ${isDark ? "dark" : ""}`} data-testid="landing-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={`Nutramilo · ${t.hero.headline_1} ${t.hero.headline_2}`} description={t.hero.sub} path="/" />
      {preview && (
        <div data-testid="landing-cms-preview-banner"
          className="sticky top-0 z-[60] bg-amber-500 text-black text-xs font-bold text-center py-1.5">
          Преглед на черновата — посетителите още виждат публикуваното
        </div>
      )}
      {order.map((id) => {
        if (hidden.has(id) || !SECTIONS[id]) return null;
        const node = <React.Fragment key={id}>{SECTIONS[id]}</React.Fragment>;
        return LAZY.has(id)
          ? <Suspense key={id} fallback={<WidgetSkeleton isDark={isDark} />}>{node}</Suspense>
          : node;
      })}
    </div>
  );
}

export default function LandingPage() {
  return (
    <AudienceProvider>
      <LandingPageInner />
    </AudienceProvider>
  );
}
